import { Fragment, useEffect, useState } from 'react';
import { totalDesJours } from '../../lib/pointage';
import { ListeFeuillesDepot } from '../../components/ListeFeuillesDepot.jsx';
import { BoutonDocument } from '../../components/BoutonDocument.jsx';
import { DonneesPersonnellesFiche } from '../../components/DonneesPersonnellesFiche.jsx';
import { ApercuCharges } from '../../components/ApercuCharges.jsx';
import { InvitationEmploye } from '../../components/InvitationEmploye.jsx';
import { useDemande } from '../../components/Dialogue.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FR } from '../../components/ui/FieldRow.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { SOC } from '../../constants/company';
import { PAYS_BY } from '../../constants/countries';
import { ContratSigne } from './ContratSigne.jsx';
import { ReleveHeuresDetail } from './ReleveHeuresDetail.jsx';
import { DeclarationAnnuelle } from './DeclarationAnnuelle.jsx';
import { EmpEditor } from './EmpEditor.jsx';
import { FicheMensuelle } from './FicheMensuelle.jsx';
import { LienCertificat, ProposerDate } from './ProposerDate.jsx';

import { idProvisoire } from '../../lib/identifiants';
import { nomComplet } from '../../lib/acces';
import { ACCESS_ROLE, roleCan } from '../../lib/access';
import { AttestationTravail } from './AttestationTravail.jsx';
import { DemissionRecue } from './DemissionRecue.jsx';
import { LesEquipes } from './LesEquipes.jsx';
import { MonEquipe } from './MonEquipe.jsx';
import { QuiVaSurQuelChantier } from './QuiVaSurQuelChantier.jsx';
import { equipeDe } from '../../lib/equipes';
import { useEquipes } from '../../api/useEquipes';
import { DemandesFormation, demandesEnAttente } from './DemandesFormation.jsx';
import { dernierJourLegal, joursDeProtection, verifieProtection } from '../../lib/licenciement';
import { anneeCourante, lignesDuPays, tauxApplicables } from '../../lib/charges';
import { chargesPatronales, retenuesSalariales, tauxEcrit } from '../../lib/retenues';
import { MODES_13, douziemesAcquis, mode13, treizieme } from '../../lib/salaire13';
import { CERT_TXT_DEF, genConvention } from '../../lib/contracts';
import { formationDu } from '../../lib/costs';
import { dateSuisse, joursOuvres } from '../../lib/dates';
import { DocumentsEmploye } from './DocumentsEmploye.jsx';
import { decideConge } from './decisionConge';
import { DEVISE, fmt, fmtH } from '../../lib/currency';
import { universMagasin } from '../../lib/modules';
import { CLE_LIEUX_TRAVAIL, lieuxDeLaSociete, lieuxDuReleve } from '../../lib/lieux-travail';
import { useReglages } from '../../api/reglages.jsx';
import { separeLEffectif } from '../../lib/personnel';
import { ajouteAuFil, marqueLusLocalement, nonLus } from '../../lib/messages-employe';
import { employesApi, formationsApi } from '../../api/resources';
import { useMaFiche } from '../../api/maFiche';
import { dtoVersEmploye, dtoVersMessageEmploye } from '../../api/mappers';
import { printDoc } from '../../lib/print';
import { hours, uid, aujourdhuiIso } from '../../lib/utils';
import { C, EMP_COLORS, docAcc } from '../../theme/palette';
import { localeDates, tr, trf } from '../../lib/i18n';
import { tamponVoulu } from '../../lib/tampon';

/** Sorte de pièce (Réglages → Documents) de chaque impression RH. */
const SORTE_PAR_IMPRESSION = {
  salaire: 'ficheSalaire',
  certsalaire: 'certificatSalaire',
  certificat: 'certificatTravail',
  licenciement: 'licenciement',
};

export function EquipePage({
  onNav,
  soc,
  employes,
  setEmployes,
  rechargeEmployes,
  chantiers,
  conges,
  setConges,
  estMixte,
  univers,
  setUnivers,
  ouvrir,
  onOuvert,
  feuilles = [],
}) {
  // Au magasin, on ne pointe pas sur un chantier : la journee se pointe sur
  // la fiche de l'employe (`badges`), sans chantier. La feuille de presence
  // et le releve d'heures s'y lisent ; les chantiers travailles n'y ont pas
  // de sens.
  const magE = universMagasin({ soc, estMixte, univers });
  const { reglageJson } = useReglages();
  const depotAdresse = lieuxDeLaSociete(reglageJson(CLE_LIEUX_TRAVAIL, {})).depotAdresse;
  /**
   * Les lieux où l'employé a pointé, pour le relevé d'heures : ses chantiers
   * au bâtiment ; au magasin, le magasin lui-même, avec les journées pointées
   * sur sa fiche.
   *
   * <p>Au bâtiment aussi, une journée pointée sans chantier (aucun chantier
   * prévu ce jour-là) reste sur la fiche de l'employé. Elle compte dans le
   * salaire : le relevé doit la montrer, sinon il ne tombe pas juste. Le
   * bureau, le dépôt et le télétravail ont chacun leur lieu.
   */
  const lieuxDe = (emp) => lieuxDuReleve(emp, chantiers, { soc, depotAdresse, magasin: magE });
  // Demande de conge a laquelle la direction propose une autre date.
  const [proposeC, setProposeC] = useState(null);
  const [selId, setSelId] = useState(null);
  const [showMens, setShowMens] = useState(false);
  const [tab, setTab] = useState('fiche');
  const [msgRep, setMsgRep] = useState('');
  const [msgEnvoi, setMsgEnvoi] = useState(false);
  const [msgErr, setMsgErr] = useState('');
  const voitSalaire =
    ACCESS_ROLE === 'patron' ||
    ACCESS_ROLE === 'comptable' ||
    (soc?.permSalaire || {})[ACCESS_ROLE] === true;
  const tabEff = !voitSalaire && tab === 'fiche' ? 'pointages' : tab;
  const [empForm, setEmpForm] = useState(null);
  const [printMode, setPrintMode] = useState(null);
  // Signature du patron et tampon au-dessus de la ligne « employeur », quand
  // Réglages → Documents les veut sur cette sorte de pièce.
  const imagesEmployeur = (sorte) =>
    sorte && tamponVoulu(soc, sorte) && (soc?.tampon || soc?.sigPatron) ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          marginBottom: 4,
        }}
      >
        {soc?.tampon && (
          <img
            src={soc.tampon}
            alt="tampon"
            style={{ width: 64, height: 64, objectFit: 'contain' }}
          />
        )}
        {soc?.sigPatron && (
          <img src={soc.sigPatron} alt="signature" style={{ height: 40, objectFit: 'contain' }} />
        )}
      </div>
    ) : null;
  // Ouvrir le fil d'un employé : ses messages se lisent, pour le compte qui
  // les ouvre seulement — la secrétaire qui lit n'éteint pas la cloche du
  // patron.
  const ouvreOnglet = (t, emp) => {
    setTab(t);
    if (t === 'messages' && emp && nonLus(emp) > 0) {
      setEmployes((p) => marqueLusLocalement(p, emp.id));
      if (typeof emp.id === 'number') {
        employesApi.marqueMessagesLus(emp.id).catch(() => {});
      }
    }
  };
  // La notification d'un message mène droit à la conversation de l'employé.
  useEffect(() => {
    if (!ouvrir?.empId) return;
    const emp = (employes || []).find((e) => e.id === ouvrir.empId);
    if (!emp) return;
    setSelId(emp.id);
    setShowMens(false);
    setEmpForm(null);
    setPrintMode(null);
    ouvreOnglet(ouvrir.onglet || 'fiche', emp);
    if (onOuvert) onOuvert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvrir, employes]);
  // Les équipes par technicien : le patron les règle ; un chef d'équipe
  // limité ne reçoit du serveur que ses employés et ses chantiers.
  const { equipes, limite: equipeLimitee, setEquipes, recharge: rechargeEquipes } = useEquipes();
  const [quiVa, setQuiVa] = useState(false);
  const [voitEquipes, setVoitEquipes] = useState(false);
  // Les demandes de formation qui attendent une réponse du bureau.
  const [formationsEnAttente, setFormationsEnAttente] = useState(0);
  const relisFormations = () => {
    if (!roleCan('envoyer') || typeof formationsApi.toutes !== 'function') return;
    Promise.resolve()
      .then(() => formationsApi.toutes())
      .then((l) => setFormationsEnAttente(demandesEnAttente(l || []).length))
      .catch(() => {});
  };
  useEffect(() => {
    relisFormations();
    // À l'ouverture de la page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [annCert, setAnnCert] = useState(new Date().getFullYear());
  const [moisPaie, setMoisPaie] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  });
  const [docF, setDocF] = useState({});
  const moisDe = (d) => {
    d = '' + (d || '');
    if (d.includes('-')) return d.slice(0, 7);
    const p = d.split('.');
    if (p.length >= 3) return p[2] + '-' + p[1].padStart(2, '0');
    return '';
  };
  const [congeForm, setCongeForm] = useState(null);
  const [showTauxEq, setShowTauxEq] = useState(false);
  // Les taux de charges changent chaque année : la paie d'un mois lit ceux de
  // son année, pas ceux d'aujourd'hui.
  const anneePaie = moisPaie ? +moisPaie.slice(0, 4) : anneeCourante();
  const [showDeclaration, setShowDeclaration] = useState(false);
  const COLS = EMP_COLORS;
  // Le patron, aussi salarie de sa societe : sa fiche se rattache a son compte
  // d'un geste, sans invitation. La liste est relue du serveur — la fiche y
  // est deja, reprise ou creee — puis sa fiche s'ouvre a completer.
  const maFiche = useMaFiche({
    onRattache: async (fiche) => {
      if (rechargeEmployes) {
        await rechargeEmployes();
        return;
      }
      const mienne = dtoVersEmploye(fiche);
      setEmployes((p) => (p.some((e) => e.id === mienne.id) ? p : [...p, mienne]));
    },
  });
  const mAjouteCommeEmploye = async () => {
    const fiche = await maFiche.seRattache();
    if (!fiche) return;
    setSelId(fiche.id);
    setEmpForm({ ...dtoVersEmploye(fiche) });
    setTab('fiche');
  };
  const saveEmp = () => {
    if (empForm.id) {
      setEmployes((p) => p.map((e) => (e.id === empForm.id ? { ...e, ...empForm } : e)));
    } else {
      const id = idProvisoire();
      setEmployes((p) => [...p, { ...empForm, id }]);
    }
    setEmpForm(null);
  };
  const calc = (emp) => {
    // Les taux de l'entreprise pour l'année de la paie, chacun borné comme sur
    // la fiche officielle : une saisie hors bornes retombe sur le taux légal,
    // des deux côtés.
    const T = tauxApplicables(soc, anneePaie);
    const pts = [
      ...chantiers.flatMap((c) =>
        (c.pointages || []).filter(
          (p) => p.empId === emp.id && (!moisPaie || moisDe(p.date) === moisPaie),
        ),
      ),
      // La fiche renvoie aussi les pointages de chantier, deja comptes ci-dessus :
      // les heures et le brut etaient doubles.
      ...(emp.badges || []).filter(
        (b) => b.chId == null && (!moisPaie || moisDe(b.date) === moisPaie),
      ),
    ];
    const totalH = pts.reduce(
      (s, p) => (p.type === 'absence' ? s : s + hours(p.entree, p.sortie, p.pause)),
      0,
    );
    const suppH = pts
      // « sup » : les heures en plus saisies sur le chantier avant que le
      // type s'aligne sur celui du serveur (« supp »).
      .filter((p) => p.type === 'supp' || p.type === 'sup')
      .reduce((s, p) => s + hours(p.entree, p.sortie, p.pause), 0);
    const heuresNorm = Math.max(0, totalH - suppH);
    const txH = emp.txH || 0;
    // Toutes les heures au taux de base : les heures en plus ne sont pas
    // majorées.
    const brutHoraire = totalH * txH;
    // Pas d'heures pointees, ou pas de taux horaire : salaire mensuel fixe,
    // comme sur la fiche officielle du serveur.
    const auForfait = totalH <= 0 || txH <= 0;
    const salMois = auForfait ? emp.salBase || 0 : brutHoraire;
    // Le treizieme etait provisionne mois apres mois et jamais verse : la fiche
    // de decembre sortait sans lui, et les cotisations qui s'y rapportent
    // n'etaient jamais prelevees. Il s'ajoute au brut du mois de versement, et
    // supporte donc les memes charges que le reste.
    const sal13 = treizieme({ emp, soc, moisPaie, mensuel: salMois });
    // Payé à l'heure, l'employé touche son indemnité de vacances chaque mois :
    // elle fait partie du brut et supporte les cotisations, comme sur la fiche
    // officielle du serveur. Elle s'ajoutait au net, sans cotisations.
    const indemVac =
      !auForfait && +emp.tauxVac > 0
        ? Math.round(salMois * ((+emp.tauxVac || 0) / 100) * 100) / 100
        : 0;
    const salBrut = salMois + sal13 + indemVac;
    // Une assurance à laquelle l'employé n'est pas soumis ne retient rien,
    // comme sur la fiche officielle que le serveur calcule. Hors de Suisse,
    // les cotisations du pays, ligne par ligne.
    const r = retenuesSalariales(
      salBrut,
      emp,
      {
        avs: T.avsSal,
        ac: T.acSal,
        aanp: T.aanpSal,
        ijm: T.ijmSal,
        canton: T.cantonSal,
      },
      lignesDuPays(soc, anneePaie),
    );
    const { soumis, lpp, dAvs, dAc, dAanp, dIjm, dLpp, dCanton } = r;
    const dIS = (salBrut * (emp.is || 0)) / 100;
    const ded = dAvs + dAc + dAanp + dIjm + dLpp + dCanton + dIS;
    const joursRepas = [...new Set(pts.filter((p) => p.type !== 'absence').map((p) => p.date))]
      .length;
    const indemRepas = Math.round((+emp.fraisRepas || 0) * joursRepas * 100) / 100;
    const net = Math.max(0, salBrut - ded) + indemRepas;
    // Hors de Suisse, la part de l'employeur du pays remplace les charges suisses.
    const { pAvs, pAc, pAap, pLpp, pCanton } = r.pays
      ? { pAvs: 0, pAc: 0, pAap: 0, pLpp: 0, pCanton: r.totalEmployeur }
      : chargesPatronales(salBrut, emp, T);
    const pAf = r.pays ? 0 : (salBrut * T.afPat) / 100,
      pAdmin = r.pays ? 0 : (salBrut * T.adminPat) / 100,
      pIjm = r.pays ? 0 : (salBrut * T.ijmPat) / 100;
    // La provision ne se constitue que les mois ou le treizieme n'est pas
    // verse : la porter au cout le mois du versement le compterait deux fois.
    const prov13 = emp.sal13 && sal13 === 0 ? Math.round((salMois / 12) * 100) / 100 : 0;
    const patronal = pAvs + pAc + pAap + pAf + pAdmin + pIjm + pLpp + pCanton,
      cout = salBrut + patronal + prov13;
    return {
      pts,
      totalH,
      suppH,
      heuresNorm,
      txH,
      brutHoraire,
      auForfait,
      salMois,
      sal13,
      salBrut,
      soumis,
      lpp,
      dAvs,
      dAc,
      dAanp,
      dIjm,
      dLpp,
      dCanton,
      pays: !!r.pays,
      detail: r.detail || [],
      employeur: r.employeur || [],
      dIS,
      ded,
      net,
      indemRepas,
      indemVac,
      joursRepas,
      prov13,
      pAvs,
      pAc,
      pAap,
      pAf,
      pAdmin,
      pIjm,
      pLpp,
      pCanton,
      patronal,
      cout,
    };
  };

  // La déclaration annuelle additionne la paie de toute l'équipe : le serveur
  // la réserve à qui lit la paie, et l'écran ne la propose qu'à eux.
  if (showDeclaration && voitSalaire) {
    return (
      <DeclarationAnnuelle
        soc={soc}
        onNav={onNav}
        onClose={() => setShowDeclaration(false)}
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      />
    );
  }

  if (selId) {
    const emp = employes.find((e) => e.id === selId);
    if (!emp) return null;
    if (showMens)
      return (
        <FicheMensuelle
          emp={emp}
          chantiers={lieuxDe(emp)}
          libelleLieu={magE ? 'Lieu' : 'Chantier'}
          soc={soc}
          onClose={() => setShowMens(false)}
        />
      );
    const k = calc(emp);
    const tx = tauxApplicables(soc, anneePaie);
    const empConges = conges.filter((c) => c.empId === emp.id);
    if (printMode) {
      const _da = docAcc(soc),
        accInk = _da.accInk;
      const periode = (() => {
        const p = (moisPaie || '').split('-');
        if (p.length === 2) {
          const d = new Date(+p[0], +p[1] - 1, 1);
          return d.toLocaleDateString(localeDates(), { month: 'long', year: 'numeric' });
        }
        return new Date().toLocaleDateString(localeDates(), { month: 'long', year: 'numeric' });
      })();
      const dateEmis = new Date().toLocaleDateString('fr-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      // Le document officiel correspondant à la vue affichée, quand il en
      // existe un. La pièce imprimée depuis le navigateur ne vaut que pour la
      // relecture ; c'est celle-ci qui est conservée et qui engage.
      const documentOfficiel = (() => {
        if (printMode === 'contrat') {
          return { chemin: `/employes/${emp.id}/contrat/document` };
        }
        if (printMode === 'certsalaire') {
          return {
            chemin: `/employes/${emp.id}/certificat-de-salaire/document`,
            params: { annee: annCert },
            // Rien n'est remis à l'employé sans confirmation : émis, le
            // certificat part dans son espace et sa cloche s'allume.
            confirmation: () =>
              'Ce certificat de salaire officiel est remis à l’employé dans son espace (« Mes documents ») et sa cloche s’allume. Il ne se modifie plus. Pour relire, utilise « 📄 PDF ». Le remettre maintenant ?',
          };
        }
        if (printMode === 'certificat') {
          return {
            chemin: `/employes/${emp.id}/certificat-de-travail/document`,
            // Émis, il part aussitôt dans l'espace de l'employé : on ne
            // l'émet pas en croyant seulement le relire (« 📄 PDF » suffit).
            confirmation: () =>
              'Ce PDF officiel est remis à l’employé dans son espace (« Mes documents ») et ne se modifie plus. Pour relire, utilise « 📄 PDF ». Le remettre maintenant ?',
            corps: () => ({
              version: docF.sortie || undefined,
              dateEntree: docF.entree || emp.dateEmbauche || null,
              dateSortie: docF.sortie || null,
              appreciation: docF.appreciation,
              lieu: docF.lieu || null,
            }),
          };
        }
        if (printMode === 'licenciement') {
          return {
            chemin: `/employes/${emp.id}/resiliation/document`,
            // Émis, il part aussitôt dans l'espace de l'employé : on ne
            // l'émet pas en croyant seulement le relire (« 📄 PDF » suffit).
            confirmation: () =>
              'Ce PDF officiel est remis à l’employé dans son espace (« Mes documents ») et ne se modifie plus. Pour relire, utilise « 📄 PDF ». Le remettre maintenant ?',
            corps: () => ({
              dateContrat: docF.contrat || emp.dateEmbauche || null,
              dateEffet: docF.effet || null,
              dernierJour: docF.dernier || null,
              motif: docF.motif || null,
              lieu: docF.lieu || null,
            }),
            // Le dernier jour devient la date de départ de sa fiche, si coché.
            apres: () => {
              const depart = docF.dernier || docF.effet;
              if (docF.inscrireDepart === false || !depart || !setEmployes) return;
              setEmployes((p) =>
                p.map((e) =>
                  e.id === emp.id ? { ...e, dateDepart: depart, motifDepart: 'Licenciement' } : e,
                ),
              );
            },
          };
        }
        return null;
      })();
      const Row = (lb, v, opt = {}) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: opt.big ? '10px 0' : '7px 0',
            borderBottom: opt.last ? 'none' : '1px solid #eef0f3',
            fontSize: opt.big ? 12.5 : 11,
          }}
        >
          <span
            style={{
              fontWeight: opt.b ? 700 : 400,
              color: opt.neg ? '#B00020' : opt.b ? '#0F1A2E' : '#3a4152',
            }}
          >
            {lb}
          </span>
          <span
            style={{
              fontWeight: opt.b ? 800 : 600,
              color: opt.neg ? '#B00020' : '#0F1A2E',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {v}
          </span>
        </div>
      );
      return (
        <div
          className="doc-root"
          style={{
            height: '100%',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            background: '#4a4e57',
          }}
        >
          <style>
            {
              '.a4-sheet{width:100%;max-width:210mm}@media(max-width:520px){.a4-wrap{padding:10px 6px!important}.a4-sheet{padding:22px 16px!important}}@media print{@page{size:A4;margin:0}html,body{background:#fff!important;margin:0!important;padding:0!important}.no-print{display:none!important}.doc-root{height:auto!important;overflow:visible!important;background:#fff!important}.a4-wrap{margin:0!important;padding:0!important;background:#fff!important}.a4-sheet{box-shadow:none!important;margin:0!important;width:210mm!important;max-width:210mm!important;min-height:auto!important;padding:11mm 13mm!important}}'
            }
          </style>
          <div
            className="no-print"
            style={{
              background: accInk,
              color: '#fff',
              padding: '12px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 5,
            }}
          >
            <button
              onClick={() => setPrintMode(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ‹ Retour
            </button>
            <div style={{ display: 'flex', gap: 7 }}>
              <button
                onClick={printDoc}
                style={{
                  background: C.green,
                  border: 'none',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                📄 PDF
              </button>
              {emp.id && documentOfficiel && (
                <BoutonDocument
                  chemin={documentOfficiel.chemin}
                  params={documentOfficiel.params}
                  corps={documentOfficiel.corps}
                  confirmation={documentOfficiel.confirmation}
                  apres={documentOfficiel.apres}
                  label="📄 PDF officiel"
                  color={C.purple}
                  sm
                />
              )}
              <button
                onClick={printDoc}
                style={{
                  background: 'rgba(255,255,255,.2)',
                  border: '1px solid rgba(255,255,255,.45)',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                🖨 Imprimer
              </button>
            </div>
          </div>
          <div
            className="no-print"
            style={{
              background: '#eceef1',
              color: '#555',
              padding: '7px 14px',
              fontSize: 9,
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            📱 iPhone : touche « Imprimer / PDF » → sur l'aperçu, touche le bouton Partager (carré
            avec flèche) en haut → « Enregistrer dans Fichiers ». · 💻 Ordinateur : « Imprimer » →
            destination « Enregistrer en PDF ».
          </div>
          {printMode === 'salaire' && (
            <div
              className="no-print"
              style={{
                background: '#fff',
                borderBottom: '1px solid ' + C.border,
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 700, color: C.ink }}>📅 Mois de paie :</span>
              <input
                type="month"
                value={moisPaie}
                onChange={(e) => setMoisPaie(e.target.value)}
                style={{
                  border: '1px solid ' + C.border,
                  borderRadius: 8,
                  padding: '7px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              />
            </div>
          )}
          {(printMode === 'contrat' ||
            printMode === 'certificat' ||
            printMode === 'licenciement') && (
            <div
              className="no-print"
              style={{
                background: '#fff',
                borderBottom: '1px solid ' + C.border,
                padding: '11px 14px',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: accInk,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 8,
                  textAlign: 'center',
                }}
              >
                ✏️ Complète les informations du document
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  maxWidth: 520,
                  margin: '0 auto',
                }}
              >
                <div>
                  <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>Lieu</label>
                  <input
                    value={docF.lieu || ''}
                    onChange={(e) => setDocF((p) => ({ ...p, lieu: e.target.value }))}
                    placeholder="Lausanne"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      padding: '8px',
                      fontSize: 11,
                    }}
                  />
                </div>
                {printMode === 'contrat' && (
                  <>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Date d'entrée
                      </label>
                      <input
                        type="date"
                        value={docF.entree || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, entree: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Temps d'essai
                      </label>
                      <input
                        value={docF.essai || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, essai: e.target.value }))}
                        placeholder="3 mois"
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Heures / semaine
                      </label>
                      <input
                        value={docF.heuresSem || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, heuresSem: e.target.value }))}
                        placeholder="41.5"
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Heure de début
                      </label>
                      <input
                        type="time"
                        value={docF.hDebut || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, hDebut: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Heure de fin
                      </label>
                      <input
                        type="time"
                        value={docF.hFin || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, hFin: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Salaire mensuel brut
                      </label>
                      <input
                        value={docF.salaire || ''}
                        onChange={(e) =>
                          setDocF((p) => ({
                            ...p,
                            salaire: e.target.value.replace(',', '.').replace(/[^0-9.]/g, ''),
                          }))
                        }
                        placeholder={fmt(emp.salBase || 0)}
                        inputMode="decimal"
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        IBAN (où verser le salaire)
                      </label>
                      <input
                        value={docF.iban || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, iban: e.target.value }))}
                        placeholder="CH.."
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                  </>
                )}
                {printMode === 'certificat' && (
                  <>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Date d'entrée
                      </label>
                      <input
                        type="date"
                        value={docF.entree || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, entree: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Date de sortie
                      </label>
                      <input
                        type="date"
                        value={docF.sortie || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, sortie: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                  </>
                )}
                {printMode === 'licenciement' && (
                  <>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Date du contrat
                      </label>
                      <input
                        type="date"
                        value={docF.contrat || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, contrat: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Résiliation pour le
                      </label>
                      <input
                        type="date"
                        value={docF.effet || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, effet: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                        Dernier jour de travail
                      </label>
                      <input
                        type="date"
                        value={docF.dernier || ''}
                        onChange={(e) => setDocF((p) => ({ ...p, dernier: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: '8px',
                          fontSize: 11,
                        }}
                      />
                    </div>
                    {(() => {
                      // Le dernier jour légal, proposé ; la maladie ou l'accident
                      // vérifiés avant l'envoi (art. 336c CO).
                      const remise = aujourdhuiIso();
                      const ficheEssai = { ...emp, dateEmbauche: docF.contrat || emp.dateEmbauche };
                      const propose = dernierJourLegal(ficheEssai, remise);
                      const prevu = docF.dernier || docF.effet || propose.dernierJour;
                      const protection = verifieProtection(ficheEssai, conges, remise, prevu);
                      const boite = (fond, couleur, enfant) => (
                        <div
                          style={{
                            gridColumn: '1 / -1',
                            background: fond,
                            color: couleur,
                            borderRadius: 8,
                            padding: '7px 10px',
                            fontSize: 10.5,
                            lineHeight: 1.45,
                          }}
                        >
                          {enfant}
                        </div>
                      );
                      return (
                        <>
                          {boite(
                            '#EAF3EC',
                            '#0F5532',
                            <>
                              {propose.essai
                                ? trf(
                                    'Proposé par SwissTech : temps d’essai → 7 jours, dernier jour le {0} (art. 335b CO).',
                                    dateSuisse(propose.dernierJour),
                                  )
                                : trf(
                                    'Proposé par SwissTech : {0} mois de délai, pour la fin d’un mois → dernier jour le {1} (art. 335c CO).',
                                    propose.mois,
                                    dateSuisse(propose.dernierJour),
                                  )}{' '}
                              {docF.effet !== propose.dernierJour && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDocF((p) => ({
                                      ...p,
                                      effet: propose.dernierJour,
                                      dernier: propose.dernierJour,
                                    }))
                                  }
                                  style={{
                                    border: '1px solid #0F5532',
                                    background: '#fff',
                                    color: '#0F5532',
                                    borderRadius: 6,
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {tr('Mettre cette date')}
                                </button>
                              )}
                            </>,
                          )}
                          {protection.nul &&
                            boite(
                              '#FBEDEB',
                              '#8E2B22',
                              trf(
                                '⛔ Maladie ou accident noté du {0} au {1} : un licenciement remis maintenant ne vaut pas (art. 336c CO). Attends la fin de l’absence ou de la période de protection ({2} jours).',
                                dateSuisse(protection.nul.debut),
                                dateSuisse(protection.nul.fin),
                                joursDeProtection(ficheEssai, remise),
                              ),
                            )}
                          {!protection.nul &&
                            protection.joursSuspendus > 0 &&
                            boite(
                              '#FBF1E2',
                              '#7A4A0C',
                              trf(
                                '⚠️ Maladie ou accident pendant le délai : il est suspendu de {0} jours (art. 336c CO). Le dernier jour passe au {1}.',
                                protection.joursSuspendus,
                                dateSuisse(protection.nouveauDernierJour),
                              ),
                            )}
                          <label
                            style={{
                              gridColumn: '1 / -1',
                              display: 'flex',
                              gap: 8,
                              alignItems: 'center',
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={docF.inscrireDepart !== false}
                              onChange={(e) =>
                                setDocF((p) => ({ ...p, inscrireDepart: e.target.checked }))
                              }
                              style={{ width: 18, height: 18 }}
                            />
                            {trf(
                              'Inscrire le {0} comme date de départ sur sa fiche',
                              dateSuisse(docF.dernier || docF.effet || propose.dernierJour),
                            )}
                          </label>
                        </>
                      );
                    })()}
                    {/* Le délai de la loi, vérifié avant l'envoi : la lettre se
                        disait conforme pour n'importe quelle date. */}
                    {docF.effet &&
                      docF.effet <
                        dernierJourLegal(
                          { ...emp, dateEmbauche: docF.contrat || emp.dateEmbauche },
                          aujourdhuiIso(),
                        ).dernierJour && (
                        <div
                          role="alert"
                          style={{
                            gridColumn: '1 / -1',
                            background: C.amberL,
                            color: C.ink,
                            borderRadius: 8,
                            padding: '7px 10px',
                            fontSize: 10,
                          }}
                        >
                          {trf(
                            '⚠️ Délai de congé (art. 335b et 335c CO) : au plus tôt le {0}.',
                            dateSuisse(
                              dernierJourLegal(
                                { ...emp, dateEmbauche: docF.contrat || emp.dateEmbauche },
                                aujourdhuiIso(),
                              ).dernierJour,
                            ),
                          )}
                        </div>
                      )}
                    {docF.effet && docF.dernier && docF.dernier > docF.effet && (
                      <div
                        role="alert"
                        style={{
                          gridColumn: '1 / -1',
                          background: C.amberL,
                          color: C.ink,
                          borderRadius: 8,
                          padding: '7px 10px',
                          fontSize: 10,
                        }}
                      >
                        {tr('⚠️ Le dernier jour de travail tombe après la date de résiliation.')}
                      </div>
                    )}
                  </>
                )}
              </div>
              {printMode === 'certificat' && (
                <div style={{ maxWidth: 520, margin: '10px auto 0' }}>
                  <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                    ✏️ Texte d'appréciation (modifiable)
                  </label>
                  <textarea
                    value={docF.appreciation !== undefined ? docF.appreciation : CERT_TXT_DEF(emp)}
                    onChange={(e) => setDocF((p) => ({ ...p, appreciation: e.target.value }))}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      minHeight: 90,
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      padding: '9px',
                      fontSize: 11,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}
              {printMode === 'contrat' && (
                <div style={{ maxWidth: 520, margin: '10px auto 0' }}>
                  <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                    ✏️ Clauses particulières (optionnel — ajoutées au contrat)
                  </label>
                  <textarea
                    value={docF.clauses || ''}
                    onChange={(e) => setDocF((p) => ({ ...p, clauses: e.target.value }))}
                    placeholder={
                      magE
                        ? 'Ex. véhicule de fonction, prime de vente, clause de non-concurrence…'
                        : 'Ex. véhicule de fonction, prime de chantier, clause de non-concurrence…'
                    }
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      minHeight: 70,
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      padding: '9px',
                      fontSize: 11,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}
              {printMode === 'licenciement' && (
                <div style={{ maxWidth: 520, margin: '10px auto 0' }}>
                  <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>
                    ✏️ Motif / précisions (optionnel)
                  </label>
                  <textarea
                    value={docF.motif || ''}
                    onChange={(e) => setDocF((p) => ({ ...p, motif: e.target.value }))}
                    placeholder="Ex. réorganisation, fin de mandat… (facultatif — le motif n'est pas obligatoire en Suisse)"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      minHeight: 60,
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      padding: '9px',
                      fontSize: 11,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <div
            className="a4-wrap"
            style={{ padding: '18px 12px', display: 'flex', justifyContent: 'center' }}
          >
            <div
              className="a4-sheet"
              style={{
                width: '100%',
                maxWidth: '210mm',
                minHeight: '297mm',
                background: '#fff',
                color: '#0F1A2E',
                boxSizing: 'border-box',
                padding: '11mm 13mm',
                boxShadow: '0 6px 24px rgba(0,0,0,.35)',
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
              }}
            >
              {/* En-tête société */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '12px',
                  paddingBottom: 9,
                  borderBottom: '2px solid ' + accInk,
                  marginBottom: 0,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    alignItems: 'flex-start',
                    flex: '1 1 auto',
                    minWidth: 0,
                  }}
                >
                  {soc?.logo || SOC.logo ? (
                    <img
                      src={soc?.logo || SOC.logo}
                      style={{ width: 46, height: 46, objectFit: 'contain' }}
                      alt=""
                    />
                  ) : (
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: 10,
                        background: accInk,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: 26,
                      }}
                    >
                      {(soc?.nom || SOC.nom).charAt(0)}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#0F1A2E' }}>
                      {soc?.nom || SOC.nom}
                    </div>
                    <div style={{ fontSize: 10, color: '#5a6270', marginTop: 3, lineHeight: 1.5 }}>
                      {soc?.adr || SOC.adr}
                      <br />
                      {soc?.npa || SOC.npa}
                    </div>
                    {(soc?.tel || SOC.tel) && (
                      <div style={{ fontSize: 9.5, color: '#5a6270', marginTop: 4 }}>
                        Tél. {soc?.tel || SOC.tel}
                      </div>
                    )}
                    {(soc?.email || SOC.email) && (
                      <div style={{ fontSize: 9.5, color: '#5a6270' }}>
                        {soc?.email || SOC.email}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      display: 'inline-block',
                      background: accInk,
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 14,
                      letterSpacing: 0.5,
                      padding: '7px 14px',
                      borderRadius: 6,
                    }}
                  >
                    {{
                      chantiers: "RELEVÉ D'HEURES",
                      contrat: 'CONTRAT DE TRAVAIL',
                      certificat: 'CERTIFICAT DE TRAVAIL',
                      licenciement: 'RÉSILIATION DU CONTRAT',
                      certsalaire: 'CERTIFICAT DE SALAIRE',
                      convention: 'CONVENTION DE FORMATION',
                    }[printMode] || 'FICHE DE SALAIRE'}
                  </div>
                  {printMode === 'salaire' && (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#3a4152',
                        textTransform: 'capitalize',
                        marginTop: 8,
                        fontWeight: 600,
                      }}
                    >
                      Période : {periode}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 9,
                      color: '#8a909c',
                      marginTop: printMode === 'salaire' ? 2 : 8,
                    }}
                  >
                    {printMode === 'salaire' ? 'Émise le ' + dateEmis : dateEmis}
                  </div>
                </div>
              </div>

              {/* Bandeau société complémentaire (IBAN / TVA) */}
              {(soc?.tva || SOC.tva) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 24,
                    fontSize: 8.5,
                    color: '#8a909c',
                    padding: '7px 0 0',
                  }}
                >
                  {(soc?.tva || SOC.tva) && <span>N° TVA : {soc?.tva || SOC.tva}</span>}
                </div>
              )}

              {/* Bloc employé */}
              {printMode === 'salaire' || printMode === 'contrat' ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 0,
                    border: '1px solid #e2e5ea',
                    borderRadius: 10,
                    overflow: 'hidden',
                    margin: '18px 0 22px',
                  }}
                >
                  <div style={{ padding: '13px 16px', borderRight: '1px solid #e2e5ea' }}>
                    <div
                      style={{
                        fontSize: 8,
                        color: '#8a909c',
                        textTransform: 'uppercase',
                        letterSpacing: 0.7,
                        fontWeight: 700,
                      }}
                    >
                      Employé
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 14.5, marginTop: 3 }}>
                      {[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}
                    </div>
                    <div style={{ fontSize: 10.5, color: '#5a6270', marginTop: 1 }}>{emp.role}</div>
                    {emp.avs && (
                      <div style={{ fontSize: 8.5, color: '#8a909c', marginTop: 3 }}>
                        N° AVS : {emp.avs}
                      </div>
                    )}
                    {emp.iban && (
                      <div style={{ fontSize: 8.5, color: '#8a909c', marginTop: 6 }}>
                        IBAN : {emp.iban}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '13px 16px', background: '#fafbfc' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 10,
                        padding: '2px 0',
                      }}
                    >
                      <span style={{ color: '#8a909c' }}>Taux horaire</span>
                      <b>
                        {DEVISE} {fmt(emp.txH || 0)}/h
                      </b>
                    </div>
                    {printMode === 'contrat' && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 10,
                          padding: '2px 0',
                        }}
                      >
                        <span style={{ color: '#8a909c' }}>Salaire brut / mois</span>
                        <b>
                          {DEVISE} {fmt(emp.salBase || 0)}
                        </b>
                      </div>
                    )}
                    {printMode === 'salaire' && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 10,
                          padding: '2px 0',
                        }}
                      >
                        <span style={{ color: '#8a909c' }}>Heures du mois</span>
                        <b>{fmtH(k.totalH)}</b>
                      </div>
                    )}
                    {printMode === 'salaire' && k.suppH > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 10,
                          padding: '2px 0',
                        }}
                      >
                        <span style={{ color: '#8a909c' }}>dont supplémentaires</span>
                        <b>{fmtH(k.suppH)}</b>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ margin: '18px 0 4px' }} />
              )}

              {printMode === 'salaire' && (
                <>
                  {/* Gains — calcul détaillé */}
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: accInk,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      marginBottom: 2,
                    }}
                  >
                    Salaire — calcul
                  </div>
                  {k.auForfait ? (
                    Row('Salaire mensuel forfaitaire', DEVISE + ' ' + fmt(emp.salBase || 0))
                  ) : (
                    <>
                      {Row(
                        'Heures travaillées (au taux de ' + DEVISE + ' ' + fmt(k.txH) + '/h)',
                        fmtH(k.totalH),
                      )}
                      {Row(
                        fmtH(k.totalH) + ' × ' + DEVISE + ' ' + fmt(k.txH) + '/h',
                        DEVISE + ' ' + fmt(k.brutHoraire),
                      )}
                    </>
                  )}
                  {Row('Salaire brut', DEVISE + ' ' + fmt(k.salBrut), { b: true, last: true })}
                  {emp.sal13 && (
                    <div style={{ fontSize: 9, color: C.grey, marginTop: 3, fontStyle: 'italic' }}>
                      {k.sal13 > 0
                        ? '13e salaire versé ce mois : ' +
                          DEVISE +
                          ' ' +
                          fmt(k.sal13) +
                          ' (compris dans le brut, cotisations prélevées dessus).'
                        : '+ 13e salaire : provision de ' +
                          DEVISE +
                          ' ' +
                          fmt(k.prov13) +
                          '/mois — ' +
                          MODES_13[mode13(soc)].libelle.toLowerCase() +
                          '.'}
                    </div>
                  )}

                  {/* Déductions */}
                  {soc?.pays && soc.pays !== 'CH' && !k.pays && (
                    <div
                      style={{
                        margin: '18px 0 4px',
                        padding: '10px 12px',
                        background: '#fff4e5',
                        border: '1px solid #f0a500',
                        borderRadius: 9,
                        fontSize: 9,
                        color: '#8a5a00',
                        lineHeight: 1.55,
                      }}
                    >
                      ⚠️ <b>Décompte conçu pour la Suisse.</b> Pour{' '}
                      {(PAYS_BY[soc.pays] && PAYS_BY[soc.pays].nom) || soc.pays}, les déductions
                      ci-dessous (AVS, AC, LAA, LPP) sont <b>suisses, à titre indicatif</b> — à
                      remplacer par les charges de ton pays, validées par un comptable local.
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: accInk,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      margin: '20px 0 2px',
                    }}
                  >
                    Déductions salarié
                  </div>
                  {/* Une assurance non soumise n'a pas de ligne, comme sur la fiche officielle. */}
                  {k.soumis.avs &&
                    Row('AVS / AI / APG (' + tx.avsSal + '%)', '− ' + DEVISE + ' ' + fmt(k.dAvs), {
                      neg: true,
                    })}
                  {k.soumis.ac &&
                    Row(
                      'Assurance chômage AC (' + tx.acSal + '%)',
                      '− ' + DEVISE + ' ' + fmt(k.dAc),
                      { neg: true },
                    )}
                  {k.soumis.aanp &&
                    Row(
                      'Assurance accident AANP (' + tx.aanpSal + '%)',
                      '− ' + DEVISE + ' ' + fmt(k.dAanp),
                      { neg: true },
                    )}
                  {k.dIjm > 0 &&
                    Row(
                      'Indemnités journalières maladie IJM (' + tx.ijmSal + '%)',
                      '− ' + DEVISE + ' ' + fmt(k.dIjm),
                      { neg: true },
                    )}
                  {k.pays
                    ? k.detail.map((d) => (
                        <Fragment key={d.libelle}>
                          {Row(
                            d.libelle + ' (' + tauxEcrit(d.taux) + '%)',
                            '− ' + DEVISE + ' ' + fmt(d.montant),
                            { neg: true },
                          )}
                        </Fragment>
                      ))
                    : k.dCanton > 0 &&
                      Row(
                        'Autres cotisations sociales (' + tx.cantonSal + '%)',
                        '− ' + DEVISE + ' ' + fmt(k.dCanton),
                        { neg: true },
                      )}
                  {k.soumis.lpp &&
                    Row(
                      'Prévoyance LPP 2e pilier (' + k.lpp + '%)',
                      '− ' + DEVISE + ' ' + fmt(k.dLpp),
                      { neg: true },
                    )}
                  {k.dIS > 0 &&
                    Row(
                      'Impôt à la source (' + (emp.is || 0) + '%)',
                      '− ' + DEVISE + ' ' + fmt(k.dIS),
                      { neg: true },
                    )}
                  {Row('Total des déductions', '− ' + DEVISE + ' ' + fmt(k.ded), {
                    b: true,
                    neg: true,
                    last: true,
                  })}

                  {/* Net à payer */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '6px 14px',
                      background: accInk,
                      color: '#fff',
                      borderRadius: 12,
                      padding: '18px 22px',
                      marginTop: 24,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>Net à payer</div>
                      <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,.8)', marginTop: 2 }}>
                        Salaire brut moins déductions légales
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 24,
                        fontWeight: 900,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        marginLeft: 'auto',
                      }}
                    >
                      {DEVISE} {fmt(k.net)}
                    </span>
                  </div>
                  {emp.iban && (
                    <div
                      style={{ fontSize: 10, color: '#5a6270', marginTop: 12, textAlign: 'right' }}
                    >
                      Versement sur IBAN <b>{emp.iban}</b>
                    </div>
                  )}

                  {/* Charges patronales — coût employeur */}
                  <div
                    style={{
                      marginTop: 24,
                      background: '#f7f9fb',
                      border: '1px solid #e2e5ea',
                      borderRadius: 10,
                      padding: '13px 16px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: '#8a909c',
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        marginBottom: 7,
                      }}
                    >
                      Charges patronales{' '}
                      <span style={{ fontWeight: 600, textTransform: 'none' }}>
                        (à la charge de l'employeur)
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 10,
                        padding: '3px 0',
                        color: '#5a6270',
                      }}
                    >
                      <span>
                        {k.pays
                          ? k.employeur
                              .map((c) => c.libelle + ' (' + tauxEcrit(c.taux) + '%)')
                              .join(' · ')
                          : 'AVS/AC/AF + frais admin.'}
                      </span>
                      <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {DEVISE} {fmt(k.pAvs + k.pAc + k.pAf + k.pCanton + k.pAdmin)}
                      </b>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 10,
                        padding: '3px 0',
                        color: '#5a6270',
                      }}
                    >
                      <span>Accident prof. (AAP) + LPP employeur</span>
                      <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {DEVISE} {fmt(k.pAap + k.pLpp)}
                      </b>
                    </div>
                    {k.prov13 > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 10,
                          padding: '3px 0',
                          color: '#5a6270',
                        }}
                      >
                        <span>Provision 13e salaire</span>
                        <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {DEVISE} {fmt(k.prov13)}
                        </b>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 11.5,
                        padding: '8px 0 0',
                        marginTop: 5,
                        borderTop: '1.5px solid #d8dde3',
                        fontWeight: 800,
                        color: '#0F1A2E',
                      }}
                    >
                      <span>Coût total employeur</span>
                      <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {DEVISE} {fmt(k.cout)}
                      </b>
                    </div>
                  </div>
                </>
              )}

              {printMode === 'chantiers' && (
                <ReleveHeuresDetail
                  emp={emp}
                  chantiers={lieuxDe(emp)}
                  accInk={accInk}
                  magasin={magE}
                  mois={docF.mois}
                  onMois={(m) => setDocF((p) => ({ ...p, mois: m }))}
                />
              )}

              {printMode === 'salaire' &&
                (() => {
                  const droit = emp.conges || 25;
                  const anCourant = new Date().getFullYear();
                  const cs = (conges || []).filter(
                    (c) => c.empId === emp.id && c.statut !== 'Refusé',
                  );
                  const estVac = (c) => !c.type || /vac|cong/i.test(c.type);
                  const pris = cs
                    .filter((c) => c.statut === 'Approuvé' && estVac(c))
                    .reduce((a, c) => a + (c.jours || 0), 0);
                  const attente = cs
                    .filter((c) => c.statut === 'En attente' && estVac(c))
                    .reduce((a, c) => a + (c.jours || 0), 0);
                  const solde = droit - pris;
                  const fd = (iso) => {
                    if (!iso) return '';
                    const z = ('' + iso).split('-');
                    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
                  };
                  const Stat = (lb, v, col) => (
                    <div
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '11px 5px',
                        border: '1px solid #e2e5ea',
                        borderRadius: 9,
                        background: (col || '#8a909c') + '0f',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 19,
                          fontWeight: 900,
                          color: col || '#0F1A2E',
                          lineHeight: 1,
                        }}
                      >
                        {v}
                      </div>
                      <div
                        style={{
                          fontSize: 7.5,
                          color: '#8a909c',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          marginTop: 4,
                          fontWeight: 700,
                        }}
                      >
                        {lb}
                      </div>
                    </div>
                  );
                  return (
                    <div style={{ marginTop: 24 }}>
                      <div
                        style={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          color: accInk,
                          textTransform: 'uppercase',
                          letterSpacing: 0.6,
                          marginBottom: 9,
                        }}
                      >
                        Vacances &amp; congés {anCourant}
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
                        {Stat('Droit / an', droit + ' j', accInk)}
                        {Stat('Pris', pris + ' j', C.teal)}
                        {Stat('En attente', attente + ' j', C.amber)}
                        {Stat('Solde restant', solde + ' j', solde >= 0 ? C.green : C.red)}
                      </div>
                      {cs.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: 8,
                              fontWeight: 700,
                              color: '#8a909c',
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              marginBottom: 3,
                            }}
                          >
                            Détail des absences
                          </div>
                          {cs.map((c, i) => (
                            <div
                              key={i}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 10,
                                padding: '5px 0',
                                borderBottom: '1px solid #eef0f3',
                              }}
                            >
                              <span style={{ textTransform: 'capitalize' }}>
                                {c.type || 'congé'} · {fd(c.debut)}
                                {c.fin && c.fin !== c.debut ? ' → ' + fd(c.fin) : ''}
                                {c.statut && c.statut !== 'Approuvé'
                                  ? ' (' + c.statut.toLowerCase() + ')'
                                  : ''}
                              </span>
                              <b style={{ whiteSpace: 'nowrap', marginLeft: 8 }}>
                                {c.jours || 1} j
                              </b>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* ===== CONTRAT DE TRAVAIL ===== */}
              {printMode === 'contrat' &&
                (() => {
                  const H2 = (t) => (
                    <div
                      style={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: accInk,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        margin: '7px 0 2px',
                      }}
                    >
                      {t}
                    </div>
                  );
                  const P = (c) => (
                    <div
                      style={{ fontSize: 9.5, color: '#2a3140', lineHeight: 1.38, marginBottom: 2 }}
                    >
                      {c}
                    </div>
                  );
                  const B = '____________';
                  const dd = (iso) => {
                    if (!iso) return B;
                    const z = ('' + iso).split('-');
                    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
                  };
                  const lieu = docF.lieu || B;
                  return (
                    <div>
                      <div
                        style={{
                          fontSize: 9.5,
                          color: '#2a3140',
                          lineHeight: 1.4,
                          marginBottom: 5,
                        }}
                      >
                        Entre <b>{soc?.nom || SOC.nom}</b>, {soc?.adr || SOC.adr},{' '}
                        {soc?.npa || SOC.npa} (ci-après « l'employeur »),
                        <br />
                        et <b>{[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}</b>
                        {[emp.adresse, [emp.npa, emp.ville].filter(Boolean).join(' ')].filter(
                          Boolean,
                        ).length
                          ? ', ' +
                            [emp.adresse, [emp.npa, emp.ville].filter(Boolean).join(' ')]
                              .filter(Boolean)
                              .join(', ')
                          : ''}
                        {emp.avs ? ' (N° AVS ' + emp.avs + ')' : ''} (ci-après « l'employé·e »), il
                        est convenu ce qui suit :
                      </div>
                      {H2('Art. 1 — Fonction')}
                      {P(
                        <>
                          L'employé·e est engagé·e en qualité de{' '}
                          <b>{emp.role || 'collaborateur ou collaboratrice'}</b>.
                        </>,
                      )}
                      {H2('Art. 2 — Entrée en fonction')}
                      {P(
                        <>
                          Les rapports de travail débutent le{' '}
                          <b>{dd(docF.entree || emp.dateEmbauche)}</b>. Le contrat est conclu pour
                          une durée{' '}
                          {emp.typeContrat === 'CDD' || emp.dateFin
                            ? "déterminée jusqu'au " + dd(emp.dateFin)
                            : 'indéterminée'}
                          . Un temps d'essai de <b>{docF.essai || emp.essai || B}</b> s'applique.
                        </>,
                      )}
                      {H2('Art. 3 — Salaire')}
                      {P(
                        <>
                          Le salaire mensuel brut est de{' '}
                          <b>
                            {DEVISE} {fmt(docF.salaire ? +docF.salaire : emp.salBase || 0)}
                          </b>
                          {emp.txH ? (
                            <>
                              {' '}
                              (taux horaire de référence : {DEVISE} {fmt(emp.txH)}/h)
                            </>
                          ) : null}
                          , versé 12 fois l'an{emp.sal13 ? ', plus un 13e salaire' : ''}. Il est
                          payé sur le compte IBAN {docF.iban || emp.iban || soc?.iban || B}.
                        </>,
                      )}
                      {H2('Art. 4 — Durée et horaire du travail')}
                      {P(
                        <>
                          La durée hebdomadaire de travail est de{' '}
                          <b>{docF.heuresSem || emp.hSem || B}</b> heures
                          {docF.heuresSem && !isNaN(+docF.heuresSem) ? (
                            <>
                              , soit environ <b>{fmt((+docF.heuresSem * 52) / 12)}</b> heures par
                              mois
                            </>
                          ) : null}
                          {emp.taux && emp.taux !== 100
                            ? " (taux d'activité " + emp.taux + '%)'
                            : ''}
                          .
                          {docF.hDebut || docF.hFin ? (
                            <>
                              {' '}
                              L'horaire habituel est de <b>{docF.hDebut || '__:__'}</b> à{' '}
                              <b>{docF.hFin || '__:__'}</b>.
                            </>
                          ) : null}{' '}
                          Les heures supplémentaires sont compensées ou rémunérées conformément au
                          Code des obligations.
                        </>,
                      )}
                      {H2('Art. 5 — Vacances')}
                      {P(
                        <>
                          L'employé·e a droit à <b>{emp.conges || 25} jours</b> de vacances payées
                          par année civile.
                        </>,
                      )}
                      {H2('Art. 6 — Résiliation')}
                      {P(
                        <>
                          Le contrat peut être résilié par chacune des parties dans le respect des
                          délais légaux (art. 335 ss CO)
                          {emp.delaiResil ? ', soit un délai de ' + emp.delaiResil : ''}.
                        </>,
                      )}
                      {H2('Art. 7 — Assurances sociales')}
                      {P(
                        <>
                          L'employé·e est assuré·e conformément à la loi (AVS/AI/APG, AC, LPP 2e
                          pilier, LAA accidents). Les cotisations légales sont déduites du salaire.
                        </>,
                      )}
                      {docF.clauses && docF.clauses.trim() && (
                        <>
                          {H2('Art. 8 — Clauses particulières')}
                          {docF.clauses
                            .split('\n')
                            .filter((x) => x.trim())
                            .map((l, i) => P(<span key={i}>{l}</span>))}
                        </>
                      )}
                      <div style={{ fontSize: 11, color: '#2a3140', marginTop: 14 }}>
                        Fait à {docF.lieu || emp.lieu || lieu}, le{' '}
                        {dd(docF.entree || emp.dateEmbauche) !== B
                          ? dd(docF.entree || emp.dateEmbauche)
                          : dateEmis}
                        , en deux exemplaires.
                      </div>
                    </div>
                  );
                })()}

              {printMode === 'convention' &&
                (() => {
                  const f = emp.formation || {};
                  return (
                    <div style={{ marginTop: 6 }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          color: '#2a3140',
                          lineHeight: 1.7,
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {f.texte || genConvention(emp, f, soc)}
                      </div>
                      <div
                        style={{
                          marginTop: 38,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 30,
                          position: 'relative',
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          {soc?.sigPatron ? (
                            <img
                              src={soc.sigPatron}
                              alt=""
                              style={{ height: 44, display: 'block', margin: '0 auto' }}
                            />
                          ) : (
                            <div style={{ height: 44 }} />
                          )}
                          <div
                            style={{
                              borderTop: '1px solid #0F1A2E',
                              paddingTop: 5,
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            L'employeur
                            <br />
                            <span style={{ fontWeight: 400, color: '#5a6270' }}>
                              {soc?.nom || SOC.nom}
                            </span>
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          {emp.signature ? (
                            <img
                              src={emp.signature}
                              alt=""
                              style={{ height: 44, display: 'block', margin: '0 auto' }}
                            />
                          ) : (
                            <div style={{ height: 44 }} />
                          )}
                          <div
                            style={{
                              borderTop: '1px solid #0F1A2E',
                              paddingTop: 5,
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            Le travailleur
                            <br />
                            <span style={{ fontWeight: 400, color: '#5a6270' }}>
                              {[emp.prenom, emp.nom].filter(Boolean).join(' ')}
                            </span>
                          </div>
                        </div>
                        {soc?.tampon && (
                          <img
                            src={soc.tampon}
                            alt="tampon"
                            style={{
                              position: 'absolute',
                              right: 20,
                              bottom: 38,
                              width: 66,
                              height: 66,
                              objectFit: 'contain',
                              opacity: 0.85,
                              transform: 'rotate(-8deg)',
                            }}
                          />
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: '#2a3140', marginTop: 22 }}>
                        Fait en deux exemplaires, le {dateEmis}.
                      </div>
                    </div>
                  );
                })()}

              {/* ===== CERTIFICAT DE TRAVAIL ===== */}
              {printMode === 'certificat' &&
                (() => {
                  const B = '____________';
                  const dd = (iso) => {
                    if (!iso) return B;
                    const z = ('' + iso).split('-');
                    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
                  };
                  const lieu = docF.lieu || B;
                  const P = ({ children }) => (
                    <div
                      style={{
                        fontSize: 11.5,
                        color: '#2a3140',
                        lineHeight: 1.75,
                        marginBottom: 10,
                      }}
                    >
                      {children}
                    </div>
                  );
                  return (
                    <div style={{ marginTop: 6 }}>
                      <P>
                        Nous soussignés, <b>{soc?.nom || SOC.nom}</b>, certifions par la présente
                        que :
                      </P>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 800,
                          color: accInk,
                          margin: '4px 0 12px',
                        }}
                      >
                        {emp.nom}
                      </div>
                      <P>
                        a été employé·e au sein de notre entreprise en qualité de{' '}
                        <b>{emp.role || 'collaborateur ou collaboratrice'}</b>, du{' '}
                        <b>{dd(docF.entree)}</b> au <b>{dd(docF.sortie)}</b>.
                      </P>
                      {(docF.appreciation !== undefined ? docF.appreciation : CERT_TXT_DEF(emp))
                        .split('\n')
                        .filter((x) => x.trim())
                        .map((para, i) => (
                          <P key={i}>{para}</P>
                        ))}
                      <div style={{ fontSize: 11, color: '#2a3140', marginTop: 14 }}>
                        Fait à {lieu}, le {dateEmis}.
                      </div>
                    </div>
                  );
                })()}

              {/* ===== LETTRE DE LICENCIEMENT ===== */}
              {printMode === 'licenciement' &&
                (() => {
                  const B = '____________';
                  const dd = (iso) => {
                    if (!iso) return B;
                    const z = ('' + iso).split('-');
                    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
                  };
                  const lieu =
                    docF.lieu ||
                    (soc?.npa || SOC.npa || '').split(' ').slice(1).join(' ') ||
                    '____';
                  const P = ({ children }) => (
                    <div
                      style={{
                        fontSize: 11.5,
                        color: '#2a3140',
                        lineHeight: 1.75,
                        marginBottom: 10,
                      }}
                    >
                      {children}
                    </div>
                  );
                  return (
                    <div style={{ marginTop: 6 }}>
                      <div
                        style={{
                          textAlign: 'right',
                          fontSize: 11,
                          color: '#3a4152',
                          marginBottom: 16,
                        }}
                      >
                        <b>{[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}</b>
                        {emp.adresse ? (
                          <>
                            <br />
                            {emp.adresse}
                          </>
                        ) : null}
                        {emp.npa || emp.ville ? (
                          <>
                            <br />
                            {[emp.npa, emp.ville].filter(Boolean).join(' ')}
                          </>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 11, color: '#3a4152', marginBottom: 12 }}>
                        {lieu}, le {dateEmis}
                      </div>
                      <div
                        style={{ fontSize: 12, fontWeight: 800, color: accInk, marginBottom: 12 }}
                      >
                        Objet : Résiliation des rapports de travail (recommandé)
                      </div>
                      <P>Madame, Monsieur,</P>
                      <P>
                        Par la présente, nous vous notifions la résiliation de votre contrat de
                        travail conclu le <b>{dd(docF.contrat || emp.dateEmbauche)}</b>, pour le{' '}
                        <b>{dd(docF.effet)}</b>
                        {docF.effet &&
                        docF.effet >=
                          dernierJourLegal(
                            { ...emp, dateEmbauche: docF.contrat || emp.dateEmbauche },
                            aujourdhuiIso(),
                          ).dernierJour
                          ? tr(', dans le respect du délai de congé légal (art. 335c CO).')
                          : '.'}
                      </P>
                      <P>
                        Votre dernier jour de travail est ainsi fixé au <b>{dd(docF.dernier)}</b>.
                        Le décompte final de votre salaire, de vos heures et de votre solde de
                        vacances vous sera remis à cette échéance.
                      </P>
                      {docF.motif && docF.motif.trim() && <P>{docF.motif}</P>}
                      <P>
                        Votre certificat de travail vous sera délivré séparément. Nous restons à
                        votre disposition pour tout renseignement complémentaire.
                      </P>
                      <P>
                        Nous vous remercions pour votre engagement au sein de notre entreprise et
                        vous adressons, Madame, Monsieur, nos salutations distinguées.
                      </P>
                      <div style={{ fontSize: 11, color: '#2a3140', marginTop: 18 }}>
                        {soc?.nom || SOC.nom}
                      </div>
                    </div>
                  );
                })()}

              {/* ===== CERTIFICAT DE SALAIRE ANNUEL ===== */}
              {printMode === 'certsalaire' &&
                (() => {
                  const annee = annCert || new Date().getFullYear();
                  const absT = ['vacances', 'maladie', 'accident', 'autre', 'absence'];
                  const ptsAn = [
                    ...chantiers.flatMap((c) =>
                      (c.pointages || []).filter(
                        (p) => p.empId === emp.id && ('' + (p.date || '')).includes('' + annee),
                      ),
                    ),
                    ...(emp.badges || []).filter(
                      (b) => b.chId == null && ('' + (b.date || '')).includes('' + annee),
                    ),
                  ];
                  const hAn = ptsAn.reduce(
                    (a, p) => (absT.includes(p.type) ? a : a + hours(p.entree, p.sortie, p.pause)),
                    0,
                  );
                  const brutAn =
                    hAn > 0 ? Math.round(hAn * (emp.txH || 0)) : (emp.salBase || 0) * 12;
                  // Le certificat suit la meme regle que les fiches mensuelles :
                  // une annee incomplete ne donne droit qu'a la part acquise.
                  // Sans cela, le recapitulatif annuel contredisait les paies
                  // qu'il est cense resumer.
                  const p13 = emp.sal13
                    ? Math.round((brutAn / 12) * (douziemesAcquis(emp, annee) / 12))
                    : 0;
                  const brutTot = brutAn + p13;
                  // Les taux de l'année du certificat : ceux d'aujourd'hui
                  // contrediraient les fiches qu'il résume.
                  const T2 = tauxApplicables(soc, annee);
                  // Les mêmes interrupteurs que les fiches mensuelles qu'il résume.
                  const { soumis, lpp, dAvs, dAc, dAanp, dIjm, dLpp, dCanton } = retenuesSalariales(
                    brutTot,
                    emp,
                    {
                      avs: T2.avsSal,
                      ac: T2.acSal,
                      aanp: T2.aanpSal,
                      ijm: T2.ijmSal,
                      canton: T2.cantonSal,
                    },
                  );
                  const dIS = emp.soumisIS !== false ? (brutTot * (emp.is || 0)) / 100 : 0;
                  const dedAn = dAvs + dAc + dAanp + dIjm + dLpp + dCanton + dIS,
                    netAn = Math.max(0, brutTot - dedAn);
                  return (
                    <div style={{ marginTop: 6 }}>
                      <div className="no-print" style={{ marginBottom: 14, textAlign: 'center' }}>
                        <label
                          style={{ fontSize: 9, fontWeight: 700, color: '#8a909c', marginRight: 6 }}
                        >
                          Année :
                        </label>
                        <select
                          value={annCert}
                          onChange={(e) => setAnnCert(+e.target.value)}
                          style={{
                            border: '1px solid #ccc',
                            borderRadius: 7,
                            padding: '6px 10px',
                            fontSize: 12,
                          }}
                        >
                          {[0, 1, 2, 3, 4].map((o) => {
                            const y = new Date().getFullYear() - o;
                            return (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 0,
                          border: '1px solid #e2e5ea',
                          borderRadius: 10,
                          overflow: 'hidden',
                          marginBottom: 18,
                        }}
                      >
                        <div style={{ padding: '12px 14px', borderRight: '1px solid #e2e5ea' }}>
                          <div
                            style={{
                              fontSize: 8,
                              color: '#8a909c',
                              textTransform: 'uppercase',
                              letterSpacing: 0.7,
                              fontWeight: 700,
                            }}
                          >
                            Employé·e
                          </div>
                          <div style={{ fontWeight: 800, fontSize: 14, marginTop: 3 }}>
                            {[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}
                          </div>
                          <div style={{ fontSize: 10, color: '#5a6270' }}>{emp.role}</div>
                          {emp.adresse && (
                            <div style={{ fontSize: 9, color: '#8a909c', marginTop: 2 }}>
                              {emp.adresse}
                              {emp.npa ? ', ' + [emp.npa, emp.ville].filter(Boolean).join(' ') : ''}
                            </div>
                          )}
                          {emp.avs && (
                            <div style={{ fontSize: 9, color: '#8a909c', marginTop: 2 }}>
                              N° AVS : {emp.avs}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '12px 14px' }}>
                          <div
                            style={{
                              fontSize: 8,
                              color: '#8a909c',
                              textTransform: 'uppercase',
                              letterSpacing: 0.7,
                              fontWeight: 700,
                            }}
                          >
                            Période
                          </div>
                          <div style={{ fontWeight: 800, fontSize: 14, marginTop: 3 }}>
                            Année {annee}
                          </div>
                          <div style={{ fontSize: 9, color: '#8a909c', marginTop: 2 }}>
                            {hAn > 0 ? fmtH(hAn) + ' travaillées' : 'Salaire mensuel × 12'}
                          </div>
                        </div>
                      </div>
                      {Row('Salaire brut annuel', DEVISE + ' ' + fmt(brutAn), { b: true })}
                      {p13 > 0 && Row('13e salaire', DEVISE + ' ' + fmt(p13))}
                      {Row('Total brut annuel', DEVISE + ' ' + fmt(brutTot), { b: true })}
                      <div style={{ height: 6 }} />
                      {soumis.avs &&
                        Row(
                          'AVS / AI / APG (' + T2.avsSal + '%)',
                          '− ' + DEVISE + ' ' + fmt(dAvs),
                          { neg: true },
                        )}
                      {soumis.ac &&
                        Row(
                          'Assurance chômage AC (' + T2.acSal + '%)',
                          '− ' + DEVISE + ' ' + fmt(dAc),
                          { neg: true },
                        )}
                      {soumis.aanp &&
                        Row(
                          'Assurance accident (' + T2.aanpSal + '%)',
                          '− ' + DEVISE + ' ' + fmt(dAanp),
                          { neg: true },
                        )}
                      {dIjm > 0 &&
                        Row(
                          'Indemnités journalières maladie IJM (' + T2.ijmSal + '%)',
                          '− ' + DEVISE + ' ' + fmt(dIjm),
                          { neg: true },
                        )}
                      {dCanton > 0 &&
                        Row(
                          'Autres cotisations sociales (' + T2.cantonSal + '%)',
                          '− ' + DEVISE + ' ' + fmt(dCanton),
                          { neg: true },
                        )}
                      {soumis.lpp &&
                        Row('Prévoyance LPP (' + lpp + '%)', '− ' + DEVISE + ' ' + fmt(dLpp), {
                          neg: true,
                        })}
                      {dIS > 0 &&
                        Row(
                          'Impôt à la source (' + (emp.is || 0) + '%)',
                          '− ' + DEVISE + ' ' + fmt(dIS),
                          { neg: true },
                        )}
                      {Row('Total des déductions', '− ' + DEVISE + ' ' + fmt(dedAn), {
                        b: true,
                        neg: true,
                        last: true,
                      })}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: accInk,
                          color: '#fff',
                          borderRadius: 12,
                          padding: '16px 20px',
                          marginTop: 18,
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700 }}>
                          Net versé sur l'année {annee}
                        </div>
                        <span style={{ fontSize: 20, fontWeight: 900 }}>
                          {DEVISE} {fmt(netAn)}
                        </span>
                      </div>
                      <div
                        style={{ fontSize: 8, color: '#a0a6b0', marginTop: 14, lineHeight: 1.6 }}
                      >
                        Certificat de salaire {annee} — récapitulatif indicatif basé sur les heures
                        pointées et les paramètres enregistrés. À valider par votre fiduciaire pour
                        la déclaration fiscale (modèle officiel du certificat de salaire suisse).
                      </div>
                    </div>
                  );
                })()}

              {/* Pied de page */}
              {printMode === 'contrat' ? (
                <div
                  style={{
                    marginTop: 40,
                    paddingTop: 14,
                    borderTop: '1px solid #dde1e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    {imagesEmployeur('contrat')}
                    <div
                      style={{
                        borderTop: '1px solid #8a909c',
                        width: 170,
                        paddingTop: 5,
                        fontSize: 9,
                        color: '#8a909c',
                      }}
                    >
                      L'employeur
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        borderTop: '1px solid #8a909c',
                        width: 170,
                        paddingTop: 5,
                        fontSize: 9,
                        color: '#8a909c',
                      }}
                    >
                      L'employé·e
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    marginTop: 44,
                    paddingTop: 14,
                    borderTop: '1px solid #dde1e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                  }}
                >
                  <div
                    style={{ fontSize: 8.5, color: '#a0a6b0', lineHeight: 1.6, maxWidth: '58%' }}
                  >
                    {printMode === 'salaire'
                      ? 'Document indicatif généré par SwissTech le ' +
                        dateEmis +
                        '. Les taux et montants doivent être validés par votre fiduciaire ou caisse de compensation.'
                      : 'Document généré par SwissTech le ' + dateEmis + '.'}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {imagesEmployeur(SORTE_PAR_IMPRESSION[printMode])}
                    <div
                      style={{
                        borderTop: '1px solid #8a909c',
                        width: 160,
                        paddingTop: 5,
                        fontSize: 9,
                        color: '#8a909c',
                      }}
                    >
                      {printMode === 'salaire'
                        ? 'Signature & timbre employeur'
                        : 'Signature & timbre'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <Shell
        active="equipe"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => {
          setSelId(null);
          setTab('fiche');
        }}
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        <div
          style={{
            background:
              'linear-gradient(135deg,' + (emp.col || C.teal) + ',' + (emp.col || C.teal) + 'bb)',
            borderRadius: 14,
            padding: 16,
            marginBottom: 12,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              background: 'rgba(255,255,255,.2)',
              borderRadius: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 900,
              color: '#fff',
              margin: '0 auto 8px',
            }}
          >
            {/* L'initiale du prénom, comme la liste de l'équipe. */}
            {(emp.prenom || emp.nom || '?').charAt(0)}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#fff' }}>
            {/* Prénom et nom, comme la maquette : « Marco Rossi », pas « Rossi ». */}
            {[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.7)' }}>
            {/* Le taux horaire suit « Voir les salaires » : sans ce droit, le
                serveur ne l'envoie pas et la case affichait CHF 0.00/h. */}
            {[emp.role, voitSalaire ? DEVISE + ' ' + fmt(emp.txH) + '/h' : '']
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: voitSalaire ? '1fr 1fr 1fr' : '1fr 1fr',
              gap: 6,
              marginTop: 12,
            }}
          >
            {[
              ['Heures', fmtH(k.totalH)],
              ['Supp.', fmtH(k.suppH)],
              ...(voitSalaire ? [['Net', DEVISE + ' ' + fmt(k.net)]] : []),
            ].map(([l, v]) => (
              <div
                key={l}
                style={{ background: 'rgba(255,255,255,.12)', borderRadius: 8, padding: '7px 4px' }}
              >
                <div style={{ fontSize: 6.5, color: 'rgba(255,255,255,.6)' }}>{l}</div>
                <div style={{ fontSize: 9, fontWeight: 800, color: '#fff' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        {voitSalaire && (
          <DemissionRecue
            emp={emp}
            setEmployes={setEmployes}
            onCertificat={({ entree, sortie }) => {
              setDocF((p) => ({ ...p, entree, sortie }));
              setPrintMode('certificat');
            }}
          />
        )}
        {emp.dateFin && (
          <div
            style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 9,
              padding: '9px 12px',
              fontSize: 11,
              fontWeight: 700,
              color: '#991b1b',
              margin: '10px 0',
              lineHeight: 1.5,
            }}
          >
            🔴 Contrat terminé le {emp.dateFin} — cet employé ne fait plus partie de l'entreprise.
            Ses documents restent accessibles ici.
          </div>
        )}
        {equipeDe(equipes, 'employe', emp.id) && (
          <div
            style={{
              margin: '4px 0 6px',
              fontSize: 12,
              fontWeight: 700,
              color: C.teal,
              background: C.tealL,
              padding: '7px 10px',
              borderRadius: 10,
            }}
          >
            👥 {equipeDe(equipes, 'employe', emp.id).nom}
            {equipeDe(equipes, 'employe', emp.id).chefNom
              ? ' · ' + trf('chef : {0}', equipeDe(equipes, 'employe', emp.id).chefNom)
              : ''}
          </div>
        )}
        {!emp.dateFin && typeof emp.id === 'number' && roleCan('envoyer') && (
          <button
            type="button"
            onClick={() => onNav('envoyer', { employeIds: [emp.id] })}
            style={{
              width: '100%',
              margin: '4px 0 10px',
              padding: '11px 12px',
              borderRadius: 10,
              border: '1.5px solid ' + C.teal,
              background: C.white,
              color: C.teal,
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            📨 {trf('Envoyer à {0}', emp.prenom || (emp.nom || '').split(' ')[0])}
          </button>
        )}
        <Tabs
          items={(voitSalaire ? [['fiche', '🧾 Fiche paie']] : []).concat([
            ['pointages', '⏱ Pointages'],
            ['conges', '🌴 Congés'],
            ['formation', '🎓 Formation'],
            ['depot', '📦 Dépôt'],
            [
              'messages',
              '💬 Messages' +
                ((emp.messages || []).filter((m) => m.de === 'employe' && !m.lu).length
                  ? ' (' +
                    (emp.messages || []).filter((m) => m.de === 'employe' && !m.lu).length +
                    ')'
                  : ''),
            ],
          ])}
          active={tabEff}
          onChange={(t) => ouvreOnglet(t, emp)}
        />
        {/*
          Une rangée, comme la maquette : « ✉️ Inviter » puis « ✎ Modifier
          l'employé ». L'invitation est un lien que le serveur tire et que le
          patron partage ; l'employe qui le suit choisit son code et entre.
        */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
          }}
        >
          <InvitationEmploye employe={emp} soc={soc} onNav={onNav} />
          {/* Le formulaire porte le salaire, le contrat, l'IBAN : il suit
              « Voir les salaires ». */}
          {voitSalaire && (
            <Btn
              label="✎ Modifier l'employé"
              sm
              ghost
              color={C.navy}
              onClick={() => setEmpForm({ ...emp })}
            />
          )}
        </div>
        {/* Le serveur réserve l'état et l'effacement à la direction. */}
        {ACCESS_ROLE === 'patron' && (
          <DonneesPersonnellesFiche
            sujet="employe"
            id={emp.id}
            nom={[emp.prenom, emp.nom].filter(Boolean).join(' ')}
          />
        )}
        {/*
          « Retirer de l'effectif » garde le dossier ; le patron peut aussi
          effacer une fiche — un ouvrier créé par erreur, un essai — avec ses
          pointages, congés et documents.
        */}
        {ACCESS_ROLE === 'patron' && (
          <div style={{ marginTop: 8 }}>
            <Btn
              label="🗑 Supprimer définitivement"
              sm
              ghost
              danger
              color={C.red}
              full
              onClick={async () => {
                const nom = [emp.prenom, emp.nom].filter(Boolean).join(' ') || 'cet employé';
                if (
                  !window.confirm(
                    'Supprimer définitivement ' +
                      nom +
                      ' ?\n\nSa fiche, ses pointages, congés et documents seront effacés. Pour garder son dossier (certificat de travail, salaires), utilise plutôt « Retirer de l’effectif ».',
                  )
                ) {
                  return;
                }
                try {
                  await employesApi.supprimeDefinitivement(emp.id);
                  setEmpForm(null);
                  setSelId(null);
                  if (rechargeEmployes) await rechargeEmployes();
                  if (maFiche.estLaMienne(emp)) await maFiche.fichePerdue();
                } catch (e) {
                  window.alert(e?.message || 'La suppression a échoué.');
                }
              }}
            />
          </div>
        )}
        {empForm && empForm.id === emp.id && (
          <div style={{ marginTop: 10 }}>
            <EmpEditor
              empForm={empForm}
              setEmpForm={setEmpForm}
              COLS={COLS}
              estPatron={maFiche.estLaMienne(emp)}
              forme={soc?.forme}
              magasin={magE}
              onSave={saveEmp}
              onDelete={() => {
                if (
                  !window.confirm(
                    'Retirer ' +
                      (emp.nom || 'cet employé') +
                      " de l'effectif ?\n\nSon dossier reste accessible dans « Anciens employés » : le certificat de travail et les décomptes de salaire doivent pouvoir être produits après le départ.",
                  )
                ) {
                  return;
                }
                setEmployes((p) => p.filter((e) => e.id !== emp.id));
                setEmpForm(null);
                setSelId(null);
              }}
            />
          </div>
        )}
        <div style={{ marginTop: 12 }} />
        {tabEff === 'formation' && !voitSalaire && (
          <Card style={{ marginTop: 11, padding: 13 }}>
            <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.5 }}>
              {emp.formation?.nom
                ? trf('🎓 Formation convenue : {0}', emp.formation.nom)
                : tr('Aucune formation convenue.')}
              <br />
              {tr("L'accord de formation (coût, engagement, convention) reste au bureau.")}
            </div>
          </Card>
        )}
        {tabEff === 'formation' &&
          voitSalaire &&
          (() => {
            const f = emp.formation || {};
            const updF = (patch) =>
              setEmployes((p) =>
                p.map((e) =>
                  e.id === emp.id ? { ...e, formation: { ...(e.formation || {}), ...patch } } : e,
                ),
              );
            const _is = {
              width: '100%',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '11px',
              fontSize: 12.5,
              boxSizing: 'border-box',
            };
            const du = formationDu(f);
            return (
              <Card>
                <Bar
                  icon="🎓"
                  title="Accord de formation"
                  color={C.purple}
                  sub="Formation payée · engagement · remboursement"
                />
                <div style={{ padding: 13 }}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: 'uppercase',
                      marginBottom: 5,
                    }}
                  >
                    Nom de la formation
                  </div>
                  <input
                    value={f.nom || ''}
                    onChange={(e) => updF({ nom: e.target.value })}
                    placeholder="Ex. permis cariste, sécurité, machine…"
                    style={_is}
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 9,
                      marginTop: 9,
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: 8.5, color: C.muted, marginBottom: 3, fontWeight: 700 }}
                      >
                        Coût payé (CHF)
                      </div>
                      <input
                        type="number"
                        value={f.cout || ''}
                        onChange={(e) => updF({ cout: +e.target.value || 0 })}
                        style={_is}
                      />
                    </div>
                    <div>
                      <div
                        style={{ fontSize: 8.5, color: C.muted, marginBottom: 3, fontWeight: 700 }}
                      >
                        Engagement (mois)
                      </div>
                      <input
                        type="number"
                        value={f.engagementMois || ''}
                        onChange={(e) => updF({ engagementMois: +e.target.value || 0 })}
                        style={_is}
                      />
                    </div>
                  </div>
                  <div
                    style={{ fontSize: 8.5, color: C.muted, margin: '9px 0 3px', fontWeight: 700 }}
                  >
                    Début de l'engagement
                  </div>
                  <input
                    type="date"
                    value={f.debut || ''}
                    onChange={(e) => updF({ debut: e.target.value })}
                    style={_is}
                  />
                  {+f.cout > 0 && +f.engagementMois > 0 && (
                    <div
                      style={{
                        background: C.amberL,
                        border: '1px solid #f0d9bd',
                        borderRadius: 10,
                        padding: '11px 13px',
                        marginTop: 11,
                      }}
                    >
                      <div style={{ fontSize: 9, fontWeight: 800, color: C.amber }}>
                        💡 S'il part maintenant, il rembourse
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: C.ink, margin: '3px 0' }}>
                        {DEVISE} {fmt(du)}
                      </div>
                      <div style={{ fontSize: 8.5, color: C.grey }}>
                        Dégressif : {fmt(Math.round(+f.cout / +f.engagementMois))} CHF « effacés »
                        par mois. Plus rien après {f.engagementMois} mois.
                      </div>
                    </div>
                  )}
                  <div
                    style={{ fontSize: 8.5, color: C.muted, margin: '11px 0 3px', fontWeight: 700 }}
                  >
                    Remboursement par mensualité (CHF / mois)
                  </div>
                  <input
                    type="number"
                    value={f.mensualite !== undefined ? f.mensualite : 500}
                    onChange={(e) => updF({ mensualite: +e.target.value || 0 })}
                    style={_is}
                  />
                  <div style={{ marginTop: 12 }}>
                    {!f.texte ? (
                      <Btn
                        label="📄 Générer la convention (auto-remplie)"
                        color={C.purple}
                        full
                        onClick={() => updF({ texte: genConvention(emp, f, soc) })}
                      />
                    ) : (
                      <div>
                        <div
                          style={{
                            fontSize: 8.5,
                            color: C.muted,
                            marginBottom: 5,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}
                        >
                          Convention (modifiable)
                        </div>
                        <textarea
                          value={f.texte}
                          onChange={(e) => updF({ texte: e.target.value })}
                          style={{
                            width: '100%',
                            minHeight: 210,
                            border: '1px solid ' + C.amber,
                            borderRadius: 10,
                            padding: 12,
                            fontSize: 10.5,
                            lineHeight: 1.65,
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 7, marginTop: 7 }}>
                          <Btn
                            label="↻ Régénérer"
                            sm
                            ghost
                            color={C.grey}
                            onClick={() => updF({ texte: genConvention(emp, f, soc) })}
                          />
                          <Btn
                            label="🗑 Effacer"
                            sm
                            ghost
                            color={C.red}
                            onClick={() => updF({ texte: '' })}
                          />
                        </div>
                        <div style={{ marginTop: 9 }}>
                          <Btn
                            label="📄 Voir / Imprimer en A4 (PDF)"
                            color={C.navy}
                            full
                            onClick={() => setPrintMode('convention')}
                          />
                        </div>
                        <div
                          style={{
                            marginTop: 13,
                            borderTop: '1px dashed ' + C.border,
                            paddingTop: 12,
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 12,
                            position: 'relative',
                          }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            {soc?.sigPatron ? (
                              <img
                                src={soc.sigPatron}
                                alt=""
                                style={{ height: 38, display: 'block', margin: '0 auto' }}
                              />
                            ) : (
                              <div
                                style={{
                                  height: 38,
                                  fontSize: 8,
                                  color: C.muted,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                (signature société)
                              </div>
                            )}
                            <div
                              style={{
                                borderTop: '1px solid ' + C.ink,
                                paddingTop: 3,
                                fontSize: 8,
                                color: C.grey,
                                fontWeight: 700,
                              }}
                            >
                              L'employeur
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            {emp.signature ? (
                              <img
                                src={emp.signature}
                                alt=""
                                style={{ height: 38, display: 'block', margin: '0 auto' }}
                              />
                            ) : (
                              <div
                                style={{
                                  height: 38,
                                  fontSize: 8,
                                  color: C.muted,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                (signature employé)
                              </div>
                            )}
                            <div
                              style={{
                                borderTop: '1px solid ' + C.ink,
                                paddingTop: 3,
                                fontSize: 8,
                                color: C.grey,
                                fontWeight: 700,
                              }}
                            >
                              Le travailleur
                            </div>
                          </div>
                          {soc?.tampon && (
                            <img
                              src={soc.tampon}
                              alt="tampon"
                              style={{
                                // Du côté de l'employeur : posé à droite, il
                                // couvrait la signature du travailleur.
                                position: 'absolute',
                                left: 4,
                                bottom: 26,
                                width: 50,
                                height: 50,
                                objectFit: 'contain',
                                opacity: 0.85,
                                transform: 'rotate(-10deg)',
                              }}
                            />
                          )}
                        </div>
                        <div style={{ fontSize: 8, color: C.muted, marginTop: 6 }}>
                          Signatures reprises du coffre (patron) et de l'employé · tampon de la
                          société. Modifiables dans Réglages → Accès.
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                    Remboursement dû en cas de démission ou de licenciement pour faute grave.
                    Apparaît automatiquement dans l'espace de l'employé. Signatures via le coffre +
                    tampon. ⚖️ À faire valider par ta fiduciaire.
                  </div>
                </div>
              </Card>
            );
          })()}
        {tabEff === 'fiche' && (
          <Card>
            <Bar
              icon="🧾"
              title="Fiche de salaire"
              color={C.green}
              sub="Taux indicatifs — Suisse"
            />
            <div style={{ padding: 13 }}>
              <div style={{ marginBottom: 12 }}>
                <Btn
                  label="🖨️ Imprimer la feuille des heures (A4 / PDF)"
                  color={C.navy}
                  full
                  onClick={() => setPrintMode('chantiers')}
                />
              </div>
              <div
                style={{
                  background: C.tealL,
                  border: '1px solid #cdeef2',
                  borderRadius: 10,
                  padding: '10px 12px',
                  marginBottom: 11,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 700, color: C.grey, flex: 1 }}>
                  🍽 Indemnité repas (CHF / jour travaillé)
                </span>
                <input
                  type="number"
                  value={emp.fraisRepas || ''}
                  onChange={(e) =>
                    setEmployes((p) =>
                      p.map((x) =>
                        x.id === emp.id ? { ...x, fraisRepas: +e.target.value || 0 } : x,
                      ),
                    )
                  }
                  placeholder="0"
                  style={{
                    width: 66,
                    border: '1px solid ' + C.border,
                    borderRadius: 8,
                    padding: '8px',
                    fontSize: 12,
                    textAlign: 'center',
                  }}
                />
              </div>
              <div
                style={{
                  background: C.greenL,
                  border: '1px solid ' + C.green + '28',
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 10,
                }}
              >
                {k.auForfait ? (
                  <FR
                    label="Salaire mensuel forfaitaire"
                    value={DEVISE + ' ' + fmt(emp.salBase || 0)}
                  />
                ) : (
                  <>
                    <FR label="Heures travaillées" value={fmtH(k.totalH)} />
                    <FR
                      label={fmtH(k.totalH) + ' × ' + DEVISE + ' ' + fmt(k.txH) + '/h'}
                      value={DEVISE + ' ' + fmt(k.brutHoraire)}
                    />
                  </>
                )}
                {k.sal13 > 0 && (
                  <FR
                    label={'13e salaire (' + MODES_13[mode13(soc)].libelle.toLowerCase() + ')'}
                    value={'+ ' + DEVISE + ' ' + fmt(k.sal13)}
                  />
                )}
                <FR
                  label="Salaire brut"
                  value={DEVISE + ' ' + fmt(k.salBrut)}
                  color={C.green}
                  bold
                  last
                />
              </div>
              <div
                style={{
                  background: C.redL,
                  border: '1px solid ' + C.red + '20',
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    color: C.red,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  Déductions salarié
                </div>
                {k.pays &&
                  k.detail.map((d) => (
                    <FR
                      key={d.libelle}
                      label={d.libelle + ' (' + tauxEcrit(d.taux) + '%)'}
                      value={'− ' + DEVISE + ' ' + fmt(d.montant)}
                    />
                  ))}
                {/* Une assurance non soumise ne retient rien : la fiche officielle l'omet. */}
                {!k.pays && (
                  <>
                    <FR
                      label={'AVS / AI / APG' + (k.soumis.avs ? ' (' + tx.avsSal + '%)' : '')}
                      value={k.soumis.avs ? '− ' + DEVISE + ' ' + fmt(k.dAvs) : 'non soumis'}
                    />
                    <FR
                      label={'Chômage AC' + (k.soumis.ac ? ' (' + tx.acSal + '%)' : '')}
                      value={k.soumis.ac ? '− ' + DEVISE + ' ' + fmt(k.dAc) : 'non soumis'}
                    />
                    <FR
                      label={'Accident AANP' + (k.soumis.aanp ? ' (' + tx.aanpSal + '%)' : '')}
                      value={
                        k.soumis.aanp
                          ? '− ' + DEVISE + ' ' + fmt(k.dAanp)
                          : k.soumis.laa
                            ? 'accidents prof. seuls'
                            : 'non soumis'
                      }
                    />
                    {k.dIjm > 0 && (
                      <FR
                        label={'IJM maladie (' + tx.ijmSal + '%)'}
                        value={'− ' + DEVISE + ' ' + fmt(k.dIjm)}
                      />
                    )}
                    {k.dCanton > 0 && (
                      <FR
                        label={'Autres cotisations sociales (' + tx.cantonSal + '%)'}
                        value={'− ' + DEVISE + ' ' + fmt(k.dCanton)}
                      />
                    )}
                    <FR
                      label={'LPP 2e pilier' + (k.soumis.lpp ? ' (' + k.lpp + '%)' : '')}
                      value={k.soumis.lpp ? '− ' + DEVISE + ' ' + fmt(k.dLpp) : 'non soumis'}
                    />
                  </>
                )}
                {k.dIS > 0 && (
                  <FR
                    label={'Impôt à la source (' + (emp.is || 0) + '%)'}
                    value={'− ' + DEVISE + ' ' + fmt(k.dIS)}
                  />
                )}
                <FR
                  label="Total déductions"
                  value={'− ' + DEVISE + ' ' + fmt(k.ded)}
                  color={C.red}
                  bold
                  last
                />
              </div>
              {k.indemRepas > 0 && (
                <div
                  style={{
                    background: C.tealL,
                    border: '1px solid #cdeef2',
                    borderRadius: 10,
                    padding: 12,
                    marginBottom: 10,
                  }}
                >
                  <FR
                    label={
                      '🍽 Indemnité repas (' +
                      k.joursRepas +
                      ' j × ' +
                      DEVISE +
                      ' ' +
                      fmt(emp.fraisRepas || 0) +
                      ')'
                    }
                    value={'+ ' + DEVISE + ' ' + fmt(k.indemRepas)}
                    color={C.teal}
                    bold
                    last
                  />
                </div>
              )}
              {k.indemVac > 0 && (
                <div
                  style={{
                    background: C.tealL,
                    border: '1px solid #cdeef2',
                    borderRadius: 10,
                    padding: 12,
                    marginBottom: 10,
                  }}
                >
                  <FR
                    label={`🌴 dont indemnité vacances (${emp.tauxVac || 0}%), comprise dans le brut`}
                    value={DEVISE + ' ' + fmt(k.indemVac)}
                    color={C.teal}
                    bold
                    last
                  />
                </div>
              )}
              <div
                style={{
                  background: 'linear-gradient(135deg,' + C.green + ',#0A5C32)',
                  borderRadius: 10,
                  padding: 14,
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,.7)' }}>Salaire net</span>
                <span style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>
                  {DEVISE} {fmt(k.net)}
                </span>
              </div>
              <div
                style={{
                  background: C.amberL,
                  border: '1px solid ' + C.amber + '28',
                  borderRadius: 10,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    color: C.amber,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  Charges patronales
                </div>
                {k.pays &&
                  k.employeur.map((c) => (
                    <FR
                      key={c.libelle}
                      label={c.libelle + ' (' + tauxEcrit(c.taux) + '%)'}
                      value={DEVISE + ' ' + fmt(c.montant)}
                    />
                  ))}
                {!k.pays && (
                  <>
                    <FR
                      label={'AVS / AI / APG' + (k.soumis.avs ? ' (' + tx.avsPat + '%)' : '')}
                      value={k.soumis.avs ? DEVISE + ' ' + fmt(k.pAvs) : 'non soumis'}
                    />
                    <FR
                      label={'Chômage AC' + (k.soumis.ac ? ' (' + tx.acPat + '%)' : '')}
                      value={k.soumis.ac ? DEVISE + ' ' + fmt(k.pAc) : 'non soumis'}
                    />
                    <FR
                      label={'Accident AAP' + (k.soumis.laa ? ' (' + tx.aapPat + '%)' : '')}
                      value={k.soumis.laa ? DEVISE + ' ' + fmt(k.pAap) : 'non soumis'}
                    />
                    <FR
                      label={'Alloc. familiales (' + tx.afPat + '%)'}
                      value={DEVISE + ' ' + fmt(k.pAf)}
                    />
                    <FR
                      label={'LPP' + (k.soumis.lpp ? ' (' + k.lpp + '%)' : '')}
                      value={k.soumis.lpp ? DEVISE + ' ' + fmt(k.pLpp) : 'non soumis'}
                    />
                    {k.prov13 > 0 && (
                      <FR label="Provision 13e salaire" value={DEVISE + ' ' + fmt(k.prov13)} />
                    )}
                    <FR
                      label={'Frais admin (' + tx.adminPat + '%)'}
                      value={DEVISE + ' ' + fmt(k.pAdmin)}
                    />
                    {k.pIjm > 0 && (
                      <FR
                        label={'IJM maladie (' + tx.ijmPat + '%)'}
                        value={DEVISE + ' ' + fmt(k.pIjm)}
                      />
                    )}
                    {k.pCanton > 0 && (
                      <FR
                        label={'Autres cotisations sociales (' + tx.cantonPat + '%)'}
                        value={DEVISE + ' ' + fmt(k.pCanton)}
                      />
                    )}
                  </>
                )}
                {k.pays && k.prov13 > 0 && (
                  <FR label="Provision 13e salaire" value={DEVISE + ' ' + fmt(k.prov13)} />
                )}
                {((soc && soc.chargesPerso) || []).map((c) =>
                  +c.pct > 0 ? (
                    <FR
                      key={c.id}
                      label={(c.label || 'Charge') + ' (' + c.pct + '%)'}
                      value={DEVISE + ' ' + fmt((k.salBrut * (+c.pct || 0)) / 100)}
                    />
                  ) : null,
                )}
                <FR
                  label="Total charges patronales"
                  value={
                    DEVISE +
                    ' ' +
                    fmt(
                      k.patronal +
                        ((soc && soc.chargesPerso) || []).reduce(
                          (a, c) => a + (k.salBrut * (+c.pct || 0)) / 100,
                          0,
                        ),
                    )
                  }
                  color={C.amber}
                  bold
                />
                <FR
                  label="Coût total employeur"
                  value={
                    DEVISE +
                    ' ' +
                    fmt(
                      k.cout +
                        ((soc && soc.chargesPerso) || []).reduce(
                          (a, c) => a + (k.salBrut * (+c.pct || 0)) / 100,
                          0,
                        ),
                    )
                  }
                  color={C.red}
                  bold
                  last
                />
              </div>
              {emp.iban && (
                <div style={{ fontSize: 9, color: C.muted, marginTop: 8 }}>
                  💳 Versement sur IBAN : <b style={{ color: C.ink }}>{emp.iban}</b>
                </div>
              )}
              <div style={{ fontSize: 8, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                Taux {anneePaie}, réglés dans Réglages → Charges sociales. Le LPP et l'impôt à la
                source se règlent par employé via « ✎ Modifier l'employé ».
              </div>
              <div style={{ marginTop: 11, borderTop: '1px dashed ' + C.border, paddingTop: 11 }}>
                <ApercuCharges soc={soc} annee={anneePaie} onNav={onNav} />
              </div>
            </div>
          </Card>
        )}
        <div style={{ marginTop: 11 }}>
          <Btn
            label="📅 Relevé mensuel complet"
            color={C.purple}
            full
            onClick={() => setShowMens(true)}
          />
        </div>
        {voitSalaire && <DocumentsEmploye empId={emp.id} />}
        {tabEff === 'fiche' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <Btn
                label="🖨 Fiche de salaire"
                color={C.navy}
                full
                onClick={() => setPrintMode('salaire')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Btn
                label="🖨 Heures (jour/jour)"
                color={C.teal}
                full
                onClick={() => setPrintMode('chantiers')}
              />
            </div>
          </div>
        )}
        {tabEff === 'fiche' && (
          <Card style={{ marginTop: 11 }}>
            <Bar
              icon="📄"
              title="Documents RH"
              color={C.purple}
              sub="Générés au nom de l'employé, avec logo et coordonnées de la société"
            />
            <div
              style={{
                padding: 12,
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 8,
              }}
            >
              {/* Le contrat est le PDF officiel du serveur : la version
                  dessinée à l'écran s'en écartait (salaire, articles, horaire). */}
              {emp.id && (
                <BoutonDocument
                  chemin={`/employes/${emp.id}/contrat/document`}
                  label="📄 Contrat de travail"
                  color={C.navy}
                  full
                />
              )}
              <Btn
                label="📜 Certificat de travail"
                color={C.green}
                full
                onClick={() => setPrintMode('certificat')}
              />
              <AttestationTravail emp={emp} voitSalaire={voitSalaire} />
              <Btn
                label="📄 Certificat de salaire annuel"
                color={C.teal}
                full
                onClick={() => setPrintMode('certsalaire')}
              />
              <Btn
                label="✉ Lettre de licenciement"
                color={C.red}
                full
                onClick={() => setPrintMode('licenciement')}
              />
              <Btn
                label="🖨 Relevé d'heures"
                color={C.teal}
                full
                onClick={() => setPrintMode('chantiers')}
              />
              {/* Signe par l'employe dans son espace : la fiche le dit. */}
              {typeof emp.id === 'number' && <ContratSigne employeId={emp.id} />}
            </div>
          </Card>
        )}
        {/* Absent de la maquette : sous les Documents RH. */}
        {tabEff === 'fiche' && emp.id && (
          <div style={{ marginTop: 11 }}>
            <BoutonDocument
              chemin={`/employes/${emp.id}/fiche-de-paie/document`}
              params={{ mois: moisPaie }}
              // Un mois en cours se figeait tel quel, et l'employé le voyait
              // aussitôt dans ses fiches : on le demande avant.
              confirmation={() =>
                moisPaie >= aujourdhuiIso().slice(0, 7)
                  ? 'Ce mois n’est pas terminé : le décompte sera figé avec les heures saisies à ce jour et remis à l’employé. Le figer maintenant ?'
                  : ''
              }
              label="📄 Décompte de salaire (PDF officiel)"
              color={C.purple}
              full
            />
            <div style={{ fontSize: 8.5, color: C.muted, marginTop: 4, lineHeight: 1.4 }}>
              Le décompte est figé au mois choisi : redemandé plus tard, il rend la même pièce.
            </div>
          </div>
        )}
        {/* Les chantiers travaillés sont du bâtiment. */}
        {tabEff === 'fiche' &&
          !magE &&
          (() => {
            const pc = chantiers
              .map((c) => {
                const pts = (c.pointages || []).filter((p) => p.empId === emp.id);
                const rs = (c.rapports || []).filter((r) =>
                  r.empId != null ? r.empId === emp.id : r.empNom === emp.nom,
                );
                const norm = (d) => {
                  d = (d || '') + '';
                  if (d.includes('-')) {
                    const z = d.split('-');
                    return {
                      k: z[0] + (z[1] || '').padStart(2, '0') + (z[2] || '').padStart(2, '0'),
                      l: (z[2] || '') + '.' + (z[1] || '') + '.' + z[0],
                    };
                  }
                  const p = d.split('.');
                  if (p.length >= 3)
                    return {
                      k: p[2] + p[1].padStart(2, '0') + p[0].padStart(2, '0'),
                      l: p[0].padStart(2, '0') + '.' + p[1].padStart(2, '0') + '.' + p[2],
                    };
                  return { k: d, l: d };
                };
                const bd = {};
                pts.forEach((p) => {
                  const z = norm(p.date),
                    k = z.k;
                  bd[k] = bd[k] || { date: z.l, k: z.k, h: 0, e: '', s: '', note: '', meteo: '' };
                  bd[k].h += hours(p.entree, p.sortie, p.pause);
                  if (p.entree) bd[k].e = p.entree;
                  if (p.sortie) bd[k].s = p.sortie;
                });
                rs.forEach((r) => {
                  const z = norm(r.date),
                    k = z.k;
                  bd[k] = bd[k] || {
                    date: z.l,
                    k: z.k,
                    h: r.heures || 0,
                    e: r.hd || '',
                    s: r.hf || '',
                    note: '',
                    meteo: '',
                  };
                  bd[k].note = r.contenu || bd[k].note;
                  bd[k].meteo = r.meteo || bd[k].meteo;
                  if (!bd[k].h && r.heures) bd[k].h = r.heures;
                });
                const days = Object.values(bd).sort((a, b) => (b.k || '').localeCompare(a.k || ''));
                return {
                  nom: c.nom,
                  adr: [c.adr, c.npa].filter(Boolean).join(', '),
                  h: totalDesJours(days),
                  n: pts.length + rs.length,
                  days,
                };
              })
              .filter((x) => x.n > 0);
            if (!pc.length) return null;
            const tot = pc.reduce((a, x) => a + x.h, 0);
            return (
              <Card style={{ marginTop: 11 }}>
                <Bar
                  icon="🏗"
                  title="Chantiers travaillés — jour par jour"
                  color={C.teal}
                  sub="Détail des heures et travaux"
                />
                <div style={{ padding: 13 }}>
                  {pc.map((x, i) => (
                    <div key={i} style={{ marginBottom: 11 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 6,
                        }}
                      >
                        <div style={{ flex: 1, marginRight: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>{x.nom}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>📍 {x.adr || '—'}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: C.teal }}>
                          {fmtH(x.h)}
                        </div>
                      </div>
                      {x.days.map((d, j) => (
                        <div
                          key={j}
                          style={{
                            borderLeft: '2px solid ' + C.border,
                            paddingLeft: 9,
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: 9.5,
                            }}
                          >
                            <span style={{ fontWeight: 700, color: C.navy }}>
                              📅 {d.date}
                              {d.e ? ' · ' + d.e + (d.s ? '→' + d.s : '') : ''}
                            </span>
                            <b style={{ color: C.teal }}>{fmtH(d.h)}</b>
                          </div>
                          {d.note && (
                            <div
                              style={{
                                fontSize: 8.5,
                                color: C.grey,
                                fontStyle: 'italic',
                                marginTop: 2,
                              }}
                            >
                              📝 {d.meteo ? d.meteo + ' · ' : ''}
                              {d.note}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 4,
                      fontSize: 11,
                      fontWeight: 800,
                      borderTop: '1px solid ' + C.border,
                      paddingTop: 9,
                    }}
                  >
                    <span>Total heures</span>
                    <span style={{ color: C.navy }}>{fmtH(tot)}</span>
                  </div>
                </div>
              </Card>
            );
          })()}
        {tabEff === 'pointages' && (
          <div>
            {k.pts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: C.muted, fontSize: 10 }}>
                Aucun pointage
              </div>
            ) : (
              k.pts.map((p) => {
                const h = hours(p.entree, p.sortie, p.pause);
                return (
                  <Card key={p.id} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        padding: '11px 13px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700 }}>{p.tache || 'Travail'}</div>
                        <div style={{ fontSize: 8.5, color: C.muted }}>
                          {p.date} · {p.entree}→{p.sortie}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 900,
                          color: p.sortie ? C.amber : C.teal,
                        }}
                      >
                        {p.sortie ? fmtH(h) : 'en cours'}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}
        {tabEff === 'conges' && (
          <div>
            {!congeForm ? (
              <div style={{ marginBottom: 10 }}>
                <Btn
                  label="➕ Noter un congé / une absence"
                  color={C.teal}
                  full
                  onClick={() => setCongeForm({ debut: '', fin: '', type: 'vacances' })}
                />
              </div>
            ) : (
              <Card style={{ marginBottom: 10 }}>
                <Bar
                  icon="🌴"
                  title="Noter un congé / une absence"
                  color={C.teal}
                  sub="Enregistré directement comme approuvé"
                />
                <div style={{ padding: 13 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Inp
                        label="Du"
                        type="date"
                        value={congeForm.debut}
                        onChange={(e) => setCongeForm((p) => ({ ...p, debut: e.target.value }))}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Inp
                        label="Au"
                        type="date"
                        value={congeForm.fin}
                        onChange={(e) => setCongeForm((p) => ({ ...p, fin: e.target.value }))}
                      />
                    </div>
                  </div>
                  <Inp label="Type">
                    <select
                      value={congeForm.type}
                      onChange={(e) => setCongeForm((p) => ({ ...p, type: e.target.value }))}
                      style={{
                        width: '100%',
                        border: '1px solid ' + C.border,
                        borderRadius: 9,
                        padding: '10px',
                        fontSize: 11,
                      }}
                    >
                      {[
                        ['vacances', 'Vacances'],
                        ['congé', 'Congé'],
                        ['congé payé', 'Congé payé'],
                        ['maladie', 'Maladie'],
                        // Comme dans l'espace de l'ouvrier.
                        ['accident', 'Accident'],
                        ['absence', 'Absence'],
                      ].map(([t, libelle]) => (
                        <option key={t} value={t}>
                          {libelle}
                        </option>
                      ))}
                    </select>
                  </Inp>
                  <div style={{ display: 'flex', gap: 7, marginTop: 4 }}>
                    <div style={{ flex: 1 }}>
                      <Btn
                        label="✓ Enregistrer"
                        color={C.green}
                        full
                        disabled={!congeForm.debut}
                        onClick={() => {
                          const deb = congeForm.debut,
                            fin = congeForm.fin || deb;
                          // Du lundi au vendredi : le samedi et le dimanche ne
                          // se comptent pas dans les jours d'absence.
                          const j = joursOuvres(deb, fin) || 1;
                          setConges((p) => [
                            ...p,
                            {
                              id: uid(),
                              empId: emp.id,
                              debut: deb,
                              fin,
                              type: congeForm.type,
                              jours: j,
                              statut: 'Approuvé',
                            },
                          ]);
                          setCongeForm(null);
                        }}
                      />
                    </div>
                    <Btn label="Annuler" ghost color={C.grey} onClick={() => setCongeForm(null)} />
                  </div>
                </div>
              </Card>
            )}
            {empConges.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: C.muted, fontSize: 10 }}>
                Aucun congé enregistré
              </div>
            ) : (
              empConges.map((c) => (
                <Card key={c.id} style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      padding: '11px 13px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700 }}>
                        {c.type} · {c.jours} j
                      </div>
                      <div style={{ fontSize: 8.5, color: C.muted }}>
                        {dateSuisse(c.debut)} → {dateSuisse(c.fin)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Pill
                        label={c.statut}
                        color={
                          c.statut === 'Approuvé'
                            ? C.green
                            : c.statut === 'Refusé'
                              ? C.red
                              : C.amber
                        }
                        size={8}
                      />
                      <Btn
                        label="🗑"
                        sm
                        ghost
                        danger
                        color={C.red}
                        onClick={() => setConges((p) => p.filter((x) => x.id !== c.id))}
                      />
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
        {tabEff === 'depot' &&
          (() => {
            // Les feuilles du dépôt de l'employé : ce qu'il a sorti, pour quel
            // chantier, et celles que le magasinier a remplies pour lui.
            const siennes = (feuilles || []).filter((f) => f.empId === emp.id);
            // Le matériel qu'il a pris au dépôt, chantier par chantier : quoi,
            // combien, quel jour, à quelle heure — inscrit par le serveur.
            const sonNom = [emp.prenom, emp.nom].filter(Boolean).join(' ').trim();
            const sorties = (chantiers || [])
              .flatMap((c) =>
                (c.materiaux || [])
                  .filter((m) => m.empNom && (m.empNom === sonNom || m.empNom === emp.nom))
                  .map((m) => ({ ...m, chNom: c.nom })),
              )
              .slice(-50)
              .reverse();
            return (
              <>
                {sorties.length > 0 && (
                  <Card style={{ marginBottom: 12 }}>
                    <Bar
                      icon="📦"
                      title="Matériel pris au dépôt"
                      color={C.teal}
                      sub="Quoi, combien, quel jour, à quelle heure, pour quel chantier"
                    />
                    <div data-testid="sorties-employe" style={{ padding: '4px 13px 10px' }}>
                      {sorties.map((m, i) => (
                        <div
                          key={m.id || i}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 8,
                            padding: '7px 0',
                            borderBottom: '1px solid ' + C.border,
                            fontSize: 10,
                          }}
                        >
                          <span style={{ minWidth: 0 }}>
                            <b>
                              {m.qte} × {m.nom || m.d}
                            </b>{' '}
                            · 🏗 {m.chNom}
                            {m.source === 'fournisseur' ? ' · 🏭' : ''}
                          </span>
                          <span style={{ color: C.muted, flexShrink: 0 }}>
                            {[m.date, m.heure].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
                <Card>
                  <Bar
                    icon="📦"
                    title="Feuilles du dépôt"
                    color={C.navy}
                    sub={
                      siennes.length
                        ? siennes.length + ' feuille' + (siennes.length > 1 ? 's' : '')
                        : 'Aucune feuille pour le moment'
                    }
                  />
                  <div style={{ padding: '0 13px 12px' }}>
                    {siennes.length === 0 ? (
                      <div style={{ fontSize: 10.5, color: C.muted, padding: '14px 0' }}>
                        Quand {emp.prenom || emp.nom} sort du matériel du dépôt, sa feuille paraît
                        ici.
                      </div>
                    ) : (
                      <ListeFeuillesDepot
                        feuilles={siennes}
                        titre="Ses sorties du dépôt"
                        max={50}
                        employes={employes}
                      />
                    )}
                  </div>
                </Card>
              </>
            );
          })()}
        {tabEff === 'messages' && (
          <Card>
            <Bar
              icon="💬"
              title={'Messages — ' + (nomComplet(emp) || emp.nom)}
              color={C.teal}
              sub="Conversation avec l'employé"
            />
            <div style={{ padding: 13 }}>
              <div style={{ maxHeight: 340, overflowY: 'auto', marginBottom: 10 }}>
                {(emp.messages || []).length === 0 ? (
                  <div
                    style={{ fontSize: 10, color: C.muted, textAlign: 'center', padding: '18px 0' }}
                  >
                    Aucun message avec cet employé.
                  </div>
                ) : (
                  (emp.messages || []).map((m, i) => {
                    const dir = m.de !== 'employe';
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: dir ? 'flex-end' : 'flex-start',
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '80%',
                            background: dir ? C.navy : C.navyXL,
                            color: dir ? '#fff' : C.ink,
                            borderRadius: 12,
                            padding: '8px 11px',
                            fontSize: 11,
                            lineHeight: 1.4,
                          }}
                        >
                          {m.texte}
                          <div
                            style={{
                              fontSize: 7.5,
                              opacity: 0.7,
                              marginTop: 3,
                              textAlign: 'right',
                            }}
                          >
                            {dir ? 'Direction' : (emp.nom || '').split(' ')[0]} ·{' '}
                            {dateSuisse(m.date)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <textarea
                value={msgRep}
                onChange={(e) => setMsgRep(e.target.value)}
                placeholder={'Répondre à ' + (emp.nom || '').split(' ')[0] + '…'}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: 60,
                  border: '1px solid ' + C.border,
                  borderRadius: 10,
                  padding: '10px',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
              <div style={{ marginTop: 8 }}>
                <Btn
                  label={msgEnvoi ? 'Envoi…' : '📨 Envoyer la réponse'}
                  color={C.navy}
                  full
                  disabled={!msgRep.trim() || msgEnvoi}
                  onClick={async () => {
                    const texte = msgRep.trim();
                    if (!texte || msgEnvoi) return;
                    if (typeof emp.id !== 'number') {
                      setMsgErr(
                        'La fiche est en cours d’enregistrement : réessaie dans un instant.',
                      );
                      return;
                    }
                    // Le message part seul : le serveur l'ajoute au fil sans
                    // le réécrire, et celui que l'employé vient d'envoyer
                    // n'est plus effacé.
                    setMsgEnvoi(true);
                    setMsgErr('');
                    try {
                      const dto = await employesApi.ecrit(emp.id, texte);
                      setEmployes((p) => ajouteAuFil(p, emp.id, dtoVersMessageEmploye(dto)));
                      setMsgRep('');
                    } catch (e) {
                      setMsgErr(e?.message || 'Le message n’a pas pu être envoyé.');
                    } finally {
                      setMsgEnvoi(false);
                    }
                  }}
                />
                {msgErr && (
                  <div role="alert" style={{ fontSize: 10, color: C.red, marginTop: 6 }}>
                    {msgErr}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </Shell>
    );
  }

  // Un employe parti quitte l'effectif sans quitter l'application : son
  // certificat de travail peut etre reclame longtemps apres (CO art. 330a) et
  // ses decomptes se conservent dix ans (CO art. 958f).
  const { effectif, anciens } = separeLEffectif(employes);
  // Une demande a laquelle la direction a propose une autre date reste a
  // suivre ici, jusqu'a la reponse de l'employe.
  const enAttente = conges.filter((c) => c.statut === 'En attente' || c.statut === 'Date proposée');
  return (
    <Shell
      active="equipe"
      onNav={onNav}
      soc={soc}
      pageTitle="👷 Équipe"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {roleCan('envoyer') && formationsEnAttente > 0 && (
        <Card style={{ marginBottom: 12, padding: 12, border: '2px solid ' + C.purple }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 8 }}>
            {trf('🎓 Demandes de formation à décider ({0})', formationsEnAttente)}
          </div>
          <DemandesFormation onChange={relisFormations} />
        </Card>
      )}
      {roleCan('envoyer') && (
        <button
          type="button"
          onClick={() => onNav('envoyer')}
          style={{
            width: '100%',
            marginBottom: 12,
            padding: '13px 14px',
            borderRadius: 12,
            border: 'none',
            background: C.teal,
            color: C.white,
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          📨 {equipeLimitee ? tr('Envoyer à mon équipe') : tr("Envoyer à l'équipe")}
          <span
            style={{ display: 'block', fontSize: 11, fontWeight: 600, opacity: 0.9, marginTop: 2 }}
          >
            {tr('Message, rendez-vous, plan, chantier du jour, consigne ou document')}
          </span>
        </button>
      )}
      {(ACCESS_ROLE === 'technicien' || ACCESS_ROLE === 'secretaire') && (
        <MonEquipe
          equipes={equipes}
          onChange={() => {
            rechargeEquipes();
            if (rechargeEmployes) rechargeEmployes();
          }}
        />
      )}
      {equipeLimitee && (
        <div
          style={{
            marginBottom: 12,
            fontSize: 12,
            color: C.ink,
            background: C.tealL,
            padding: '10px 12px',
            borderRadius: 12,
            lineHeight: 1.5,
          }}
        >
          👥{' '}
          {trf(
            '{0} : tu vois seulement ton équipe et tes chantiers. Le patron voit tout.',
            equipes.map((q) => q.nom).join(', '),
          )}
        </div>
      )}
      {(ACCESS_ROLE === 'patron' || ACCESS_ROLE === 'directeur') && (
        <Card style={{ marginBottom: 12 }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setVoitEquipes((v) => !v)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setVoitEquipes((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '13px 14px',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 17 }}>👥</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>
                  {tr('Les équipes')}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>
                  {equipes.length
                    ? trf('{0} équipe(s) · une par technicien', equipes.length)
                    : tr('Une équipe par technicien (grosses sociétés)')}
                </div>
              </div>
            </div>
            <span style={{ fontSize: 12, color: C.muted }}>{voitEquipes ? '▲' : '▼'}</span>
          </div>
          {voitEquipes && (
            <div style={{ padding: '0 13px 13px' }}>
              {quiVa ? (
                <QuiVaSurQuelChantier
                  employes={employes}
                  chantiers={chantiers}
                  equipes={equipes}
                  onFini={rechargeEquipes}
                  onAnnule={() => setQuiVa(false)}
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setQuiVa(true)}
                    style={{
                      width: '100%',
                      marginBottom: 10,
                      padding: '13px 14px',
                      borderRadius: 12,
                      border: '2px solid ' + C.teal,
                      background: C.tealL,
                      color: C.teal,
                      fontSize: 13.5,
                      fontWeight: 800,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    🏗 {tr('Qui va sur quel chantier')}
                    <span style={{ display: 'block', fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                      {tr(
                        'Chantier → technicien → employés : tu fais l’équipe ou tu l’envoies au technicien',
                      )}
                    </span>
                  </button>
                  <LesEquipes
                    key={equipes.map((q) => q.id + ':' + (q.employeIds || []).length).join(',')}
                    employes={employes}
                    chantiers={chantiers}
                    onChange={setEquipes}
                  />
                </>
              )}
            </div>
          )}
        </Card>
      )}
      {voitSalaire && (
        <Card style={{ marginBottom: 12 }}>
          <div
            onClick={() => setShowTauxEq((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '13px 14px',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 17 }}>🏛</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>
                  Charges sociales (taux)
                </div>
                <div style={{ fontSize: 8.5, color: C.muted }}>
                  Communes à toute l'équipe — se règlent par année dans Réglages
                </div>
              </div>
            </div>
            <span style={{ fontSize: 12, color: C.muted }}>{showTauxEq ? '▲' : '▼'}</span>
          </div>
          {showTauxEq && (
            <div style={{ padding: '0 13px 13px' }}>
              <ApercuCharges soc={soc} annee={anneePaie} onNav={onNav} />
            </div>
          )}
        </Card>
      )}
      {(() => {
        const tIso = aujourdhuiIso();
        // Au bâtiment, la journée se pointe sur un chantier ; au magasin, sur
        // la fiche de l'employé, sans chantier.
        const rows = effectif.map((e) => {
          const pts = magE
            ? (e.badges || []).filter((b) => b.chId == null && b.date === tIso)
            : chantiers.flatMap((c) =>
                (c.pointages || [])
                  .filter((p) => p.empId === e.id && p.date === tIso)
                  .map((p) => ({ ...p, chNom: c.nom })),
              );
          return { e, p: pts[pts.length - 1] };
        });
        return (
          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="🕒"
              title="Feuille de présence — aujourd'hui"
              color={C.teal}
              sub="Connexion employé + position GPS + heures"
            />
            <div style={{ padding: 13 }}>
              {rows.map(({ e, p }) => {
                const hh = p ? hours(p.entree, p.sortie, p.pause) : 0;
                const cible = e.heuresJour || 8;
                const okH = p && p.sortie ? hh >= cible - 0.25 : null;
                const onS = p ? p.onSiteEntree : null;
                return (
                  <div
                    key={e.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '9px 0',
                      borderBottom: '1px solid ' + C.border,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 9,
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 8,
                          background: e.col || C.teal,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 900,
                          fontSize: 11,
                          flexShrink: 0,
                        }}
                      >
                        {(e.prenom || e.nom || '?').charAt(0)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700 }}>
                          {nomComplet(e) || e.nom}
                        </div>
                        <div
                          style={{
                            fontSize: 8.5,
                            color: C.muted,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p
                            ? p.entree +
                              (p.sortie ? ' → ' + p.sortie : ' → en cours') +
                              (p.chNom ? ' · ' + p.chNom : '')
                            : 'Pas encore pointé'}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                      {p ? (
                        <>
                          <div style={{ fontSize: 10, fontWeight: 800 }}>
                            {p.sortie ? fmtH(hh) : '⏱ en cours'}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 5,
                              justifyContent: 'flex-end',
                              marginTop: 2,
                            }}
                          >
                            {onS != null && (
                              <span
                                style={{
                                  fontSize: 7.5,
                                  fontWeight: 700,
                                  color: onS ? C.green : C.red,
                                }}
                              >
                                {onS ? '✅ sur site' : '⚠ hors site'}
                              </span>
                            )}
                            {okH != null && (
                              <span
                                style={{
                                  fontSize: 7.5,
                                  fontWeight: 700,
                                  color: okH ? C.green : C.amber,
                                }}
                              >
                                {okH ? 'heures OK' : "sous l'horaire"}
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <span style={{ fontSize: 14, color: C.muted }}>—</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 7.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                {magE
                  ? 'Chaque employé se connecte avec son code et pointe son arrivée/départ depuis son espace.'
                  : "Chaque ouvrier se connecte avec son code et pointe son arrivée/départ ; la position GPS confirme s'il est sur le chantier. « sur site » = dans la zone du chantier au pointage."}
              </div>
            </div>
          </Card>
        );
      })()}
      {!empForm ? (
        <div
          onClick={() =>
            setEmpForm({
              nom: '',
              role: '',
              tel: '',
              // Ni taux ni salaire inventés : la fiche créée sans eux
              // annonçait « 55.00/h » et un net de 4'791.05.
              txH: 0,
              salBase: 0,
              conges: 25,
              lpp: 5,
              is: 0,
              sal13: false,
              iban: '',
              col: COLS[0],
            })
          }
          style={{
            background: C.navy,
            borderRadius: 12,
            padding: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 20, color: '#fff' }}>＋</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>Nouvel employé</span>
        </div>
      ) : (
        !empForm.id && (
          <div style={{ marginBottom: 12 }}>
            <EmpEditor
              empForm={empForm}
              setEmpForm={setEmpForm}
              COLS={COLS}
              onSave={saveEmp}
              magasin={magE}
            />
          </div>
        )
      )}
      {!empForm && maFiche.estPatron && (
        <div style={{ marginBottom: 12 }}>
          {maFiche.rattachee ? (
            <Btn
              label={magE ? '👷 Mon espace employé' : '👷 Mon espace terrain'}
              ghost
              color={C.teal}
              full
              onClick={() => onNav('ouvrier')}
            />
          ) : (
            <Btn
              label={maFiche.enCours ? '⏳ Rattachement…' : "➕ M'ajouter comme employé"}
              ghost
              color={C.navy}
              full
              disabled={maFiche.enCours}
              onClick={mAjouteCommeEmploye}
            />
          )}
          <div style={{ fontSize: 8.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
            {maFiche.rattachee
              ? 'Ta fiche est rattachée à ton compte : ta paie sort comme celle de chacun, et ton espace d’employé s’ouvre ici.'
              : magE
                ? 'Toi aussi, salarié de ta société : ta fiche est créée depuis ton compte (ou reprise si elle existe déjà à ton e-mail) et rattachée à ton compte patron — fiche de salaire, certificat et espace employé, sans invitation.'
                : 'Toi aussi, salarié de ta société : ta fiche est créée depuis ton compte (ou reprise si elle existe déjà à ton e-mail) et rattachée à ton compte patron — fiche de salaire, certificat et espace terrain, sans invitation.'}
          </div>
          {maFiche.erreur && (
            <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 4 }}>
              {maFiche.erreur}
            </div>
          )}
        </div>
      )}
      {enAttente.length > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar icon="📅" title="Congés en attente" color={C.amber} />
          <div style={{ padding: 13 }}>
            {enAttente.map((c) => {
              const emp = employes.find((e) => e.id === c.empId);
              return (
                <div
                  key={c.id}
                  style={{
                    padding: '7px 0',
                    borderBottom: '1px solid ' + C.border,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div
                    onClick={() => setProposeC(c)}
                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.navy }}>
                      {nomComplet(emp) || emp?.nom} · {c.type}{' '}
                      <span style={{ fontSize: 10, color: C.teal, fontWeight: 700 }}>
                        › toucher pour répondre
                      </span>
                    </div>
                    <div style={{ fontSize: 8.5, color: C.muted }}>
                      {dateSuisse(c.debut)} → {dateSuisse(c.fin)} ({c.jours} j)
                    </div>
                    {c.statut === 'Date proposée' && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginTop: 2 }}>
                        ⏳ Tu as proposé : {c.debutPropose} → {c.finPropose} · en attente de sa
                        réponse
                      </div>
                    )}
                    <LienCertificat conge={c} />
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                    {c.statut !== 'Date proposée' && (
                      <Btn
                        label="✓"
                        sm
                        color={C.green}
                        onClick={() => decideConge(c, true, setConges)}
                      />
                    )}
                    <Btn label="📅" sm ghost color={C.navy} onClick={() => setProposeC(c)} />
                    <Btn
                      label="✕"
                      sm
                      ghost
                      danger
                      color={C.red}
                      onClick={() => decideConge(c, false, setConges)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {proposeC && (
        <ProposerDate
          conge={proposeC}
          nomEmploye={employes.find((e) => e.id === proposeC.empId)?.nom}
          setConges={setConges}
          onClose={() => setProposeC(null)}
        />
      )}
      {effectif.map((emp) => {
        const k = calc(emp);
        const cP = conges
          .filter((c) => c.empId === emp.id && c.statut === 'Approuvé')
          .reduce((s, c) => s + c.jours, 0);
        return (
          <Card
            key={emp.id}
            style={{ marginBottom: 9, cursor: 'pointer' }}
            onClick={() => {
              setSelId(emp.id);
              setTab('fiche');
            }}
          >
            <div style={{ padding: '13px 14px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 11,
                      background: emp.col || C.teal,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: 16,
                    }}
                  >
                    {(emp.prenom || emp.nom || '?').charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800 }}>
                      {nomComplet(emp) || emp.nom}
                    </div>
                    <div style={{ fontSize: 9, color: C.muted }}>{emp.role}</div>
                  </div>
                </div>
                {voitSalaire && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: C.green }}>
                      {DEVISE} {fmt(k.net)}
                    </div>
                    <div style={{ fontSize: 8, color: C.muted }}>net / mois</div>
                  </div>
                )}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: voitSalaire ? 'repeat(4,1fr)' : 'repeat(3,1fr)',
                  gap: 5,
                }}
              >
                {[
                  ['Heures', fmtH(k.totalH), C.navy],
                  ['Supp.', fmtH(k.suppH), C.amber],
                  ['Congés', cP + 'j', C.teal],
                  // Le salaire et le cout ne se montrent qu'a qui les lit : le
                  // serveur les retient deja, et un « CHF 0.00 net / mois » sur
                  // la liste du technicien ne disait rien de vrai.
                  ...(voitSalaire ? [['Coût', DEVISE + ' ' + fmt(k.cout), C.red]] : []),
                ].map(([l, v, c]) => (
                  <div
                    key={l}
                    style={{
                      background: c + '10',
                      borderRadius: 8,
                      padding: '6px 4px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 6.5, color: c }}>{l}</div>
                    <div style={{ fontSize: 8.5, fontWeight: 800, color: c }}>{v}</div>
                  </div>
                ))}
              </div>
              {/* Modifier ou supprimer d'ici, sans chercher au bas de la fiche. */}
              {ACCESS_ROLE === 'patron' && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelId(emp.id);
                      setEmpForm({ ...emp });
                    }}
                    style={{
                      flex: 1,
                      border: '1px solid ' + C.border,
                      background: C.white,
                      borderRadius: 8,
                      padding: '7px',
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.navy,
                      cursor: 'pointer',
                    }}
                  >
                    {tr('✎ Modifier')}
                  </button>
                  <button
                    type="button"
                    aria-label={trf('Supprimer {0}', nomComplet(emp) || emp.nom)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const nom = nomComplet(emp) || emp.nom || tr('cet employé');
                      if (
                        !window.confirm(
                          trf(
                            'Supprimer définitivement {0} ?\n\nSa fiche, ses pointages, congés et documents seront effacés.',
                            nom,
                          ),
                        )
                      ) {
                        return;
                      }
                      try {
                        await employesApi.supprimeDefinitivement(emp.id);
                        if (rechargeEmployes) await rechargeEmployes();
                        // Sa propre fiche : son compte, resté ouvert, n'y est plus rattaché.
                        if (maFiche.estLaMienne(emp)) await maFiche.fichePerdue();
                      } catch (err) {
                        window.alert(err?.message || tr('La suppression a échoué.'));
                      }
                    }}
                    style={{
                      flex: 1,
                      border: '1px solid ' + C.red,
                      background: C.white,
                      borderRadius: 8,
                      padding: '7px',
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.red,
                      cursor: 'pointer',
                    }}
                  >
                    {tr('🗑 Supprimer')}
                  </button>
                </div>
              )}
            </div>
          </Card>
        );
      })}

      {anciens.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: C.muted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              margin: '0 3px 8px',
            }}
          >
            📦 Anciens employés
          </div>
          <div style={{ fontSize: 9, color: C.grey, margin: '0 3px 10px', lineHeight: 1.5 }}>
            Ils ne font plus partie de l&apos;effectif, mais leur dossier reste accessible : le
            certificat de travail peut être réclamé longtemps après le départ, et leurs documents
            restent accessibles 15 ans, au-delà du minimum légal de dix ans.
          </div>
          {anciens.map((emp) => (
            <Card
              key={emp.id}
              style={{ marginBottom: 8, cursor: 'pointer', opacity: 0.9 }}
              onClick={() => {
                setSelId(emp.id);
                setTab('fiche');
              }}
            >
              <div
                style={{
                  padding: '11px 13px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: C.grey,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {(emp.nom || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>
                      {nomComplet(emp) || emp.nom}
                    </div>
                    <div style={{ fontSize: 9, color: C.muted }}>Parti le {emp.dateFin}</div>
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.navy,
                    background: C.navyXL,
                    borderRadius: 7,
                    padding: '5px 9px',
                    flexShrink: 0,
                  }}
                >
                  📁 Dossier ›
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
      {/* Absente de la maquette : sous la liste, pour ne rien en déplacer. */}
      {voitSalaire && (
        <Card
          style={{ marginTop: 12, marginBottom: 12, cursor: 'pointer' }}
          onClick={() => setShowDeclaration(true)}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '13px 14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <span style={{ fontSize: 17 }}>📊</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>
                  Déclaration annuelle des salaires
                </div>
                <div style={{ fontSize: 8.5, color: C.muted }}>
                  Salaires AVS, AC, LPP, impôt à la source — exports CSV, XML ELM 5, PDF et
                  certificats
                </div>
              </div>
            </div>
            <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>›</span>
          </div>
        </Card>
      )}
    </Shell>
  );
}
